import VariableManager from './VariableManager';
import LocalizedContent from './LocalizedContent';
import LocalizationKeyPicker from './LocalizationKeyPicker';
import QuickReplyTitle from './QuickReplyTitle';
import EditableNodeTitle from './EditableNodeTitle';
import DynamicChatSettings from './DynamicChatSettings';
import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import FlowDebuggerPanel from './FlowDebuggerPanel';
import PromptEditorModal from './PromptEditorModal';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';

/** Purpose: render a labeled property-editor field and optional help. Why: consistent field framing makes diverse node settings easier to scan. */
const Field = ({ label, children, helpText, id }) => (
  <label id={id} className="block scroll-mt-6">
    <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 dark:text-slate-500">
      {label}
    </div>
    {children}
    {helpText ? <div className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-500">{helpText}</div> : null}
  </label>
);

const inputClass =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 hover:border-slate-300 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-100 dark:placeholder:text-slate-500 dark:hover:border-white/20 dark:focus:border-cyan-400/50 dark:focus:ring-cyan-500/20';

const selectClass =
  'w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition hover:border-slate-300 focus:border-cyan-500 focus:ring-4 focus:ring-cyan-500/10 dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-100 dark:hover:border-white/20 dark:focus:border-cyan-400/50 dark:focus:ring-cyan-500/20';

/** Purpose: render a styled boolean control. Why: node editors share one accessible checkbox presentation. */
const Checkbox = ({ label, checked, onChange }) => (
  <label className="flex cursor-pointer items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50/40 dark:border-white/10 dark:bg-slate-900/80 dark:text-slate-200 dark:hover:border-cyan-400/30 dark:hover:bg-cyan-500/5">
    <input
      type="checkbox"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
      className="h-4 w-4 rounded border-slate-300 bg-white text-cyan-600 focus:ring-cyan-500/30 dark:border-white/20 dark:bg-slate-950 dark:text-cyan-500"
    />
    <span>{label}</span>
  </label>
);

/** Purpose: clamp a date validation day limit to supported values. Why: invalid legacy values should not make the date editor unusable. */
const normalizeDayLimit = (value) => (
  value !== null
  && value !== undefined
  && value !== ''
  && Number.isInteger(Number(value))
  && Number(value) >= 0
    ? Number(value)
    : null
);

/** Purpose: normalize date validation rules and defaults. Why: all related controls must read and write a predictable config shape. */
const normalizeDateValidation = (value = {}) => ({
  allowToday: value?.allowToday !== false,
  allowPast: value?.allowPast !== false,
  allowFuture: value?.allowFuture !== false,
  maxPastDays: normalizeDayLimit(value?.maxPastDays),
  maxFutureDays: normalizeDayLimit(value?.maxFutureDays),
});

/** Purpose: edit date-specific constraints for an input node. Why: date rules need controls beyond the generic validation selector. */
function DateValidationEditor({ value, onChange }) {
  const rules = normalizeDateValidation(value);
  /** Purpose: Merge and normalize a date-validation edit. Why: Changing one restriction must retain the other rules. */
  const updateRules = (patch) => onChange(normalizeDateValidation({ ...rules, ...patch }));
  const noDatesAllowed = !rules.allowToday && !rules.allowPast && !rules.allowFuture;

  return (
    <div className="space-y-3 rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-50/60 p-4 shadow-sm dark:border-cyan-400/15 dark:from-cyan-500/10 dark:to-sky-500/5">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-700 dark:text-cyan-100">Selectable dates</div>
      </div>
      <Checkbox
        label="Allow today's date"
        checked={rules.allowToday}
        onChange={(checked) => updateRules({ allowToday: checked })}
      />
      <Checkbox
        label="Allow past dates"
        checked={rules.allowPast}
        onChange={(checked) => updateRules({ allowPast: checked })}
      />
      {rules.allowPast ? (
        <Field label="Maximum past days">
          <input
            type="number"
            min="0"
            step="1"
            value={rules.maxPastDays ?? ''}
            onChange={(event) => updateRules({
              maxPastDays: event.target.value === '' ? null : Math.max(0, Math.floor(Number(event.target.value) || 0)),
            })}
            className={inputClass}
            placeholder="No limit"
          />
        </Field>
      ) : null}
      <Checkbox
        label="Allow future dates"
        checked={rules.allowFuture}
        onChange={(checked) => updateRules({ allowFuture: checked })}
      />
      {rules.allowFuture ? (
        <Field label="Maximum future days">
          <input
            type="number"
            min="0"
            step="1"
            value={rules.maxFutureDays ?? ''}
            onChange={(event) => updateRules({
              maxFutureDays: event.target.value === '' ? null : Math.max(0, Math.floor(Number(event.target.value) || 0)),
            })}
            className={inputClass}
            placeholder="No limit"
          />
        </Field>
      ) : null}
      {noDatesAllowed ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs leading-5 text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200">
          No date is currently selectable. Enable today, past, or future dates.
        </div>
      ) : null}
    </div>
  );
}

/** Purpose: parse optional JSON editor content with a fallback. Why: partially typed JSON must not crash the node property panel. */
const safeJsonParse = (value, fallback = {}) => {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    return fallback;
  }
};

const PANEL_CARD_CLASS = 'rounded-[28px] border border-slate-200 bg-white p-5 shadow-[0_16px_45px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-slate-900/80 dark:shadow-[0_18px_40px_rgba(2,6,23,0.32)]';
const GUIDE_CARD_CLASS = 'rounded-3xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-50 p-4 shadow-sm dark:border-cyan-400/15 dark:from-cyan-500/10 dark:to-sky-500/5';

const MEDIA_NODE_KEYS = new Set(['image', 'video', 'file']);
const QUICK_REPLY_KEYS = new Set(['quick_replies', 'message_quick_replies']);
const LIST_KEYS = new Set(['whatsapp_list_prompt', 'message_whatsapp_list', 'message_carousel', 'carousel_prompt', 'product_catalogue']);
const CONVERSATION_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी (Hindi)' },
  { code: 'hi-en', label: 'Hinglish' },
  { code: 'bn', label: 'বাংলা (Bengali)' },
  { code: 'mr', label: 'मराठी (Marathi)' },
  { code: 'gu', label: 'ગુજરાતી (Gujarati)' },
  { code: 'ta', label: 'தமிழ் (Tamil)' },
  { code: 'te', label: 'తెలుగు (Telugu)' },
  { code: 'kn', label: 'ಕನ್ನಡ (Kannada)' },
  { code: 'ml', label: 'മലയാളം (Malayalam)' },
  { code: 'pa', label: 'ਪੰਜਾਬੀ (Punjabi)' },
  { code: 'ur', label: 'اردو (Urdu)' },
];

/** Purpose: normalize variable scope labels for property controls. Why: variable pickers and templates must agree on legacy and current scope names. */
const normalizeVariableScope = (scope = '') => {
  const normalized = String(scope || '').trim().toLowerCase();
  if (normalized === 'global') return 'global';
  if (normalized === 'system') return 'system';
  if (normalized === 'session' || normalized === 'user') return 'session';
  return 'journey';
};

/** Purpose: format a scoped variable token for templates. Why: request and message editors need the exact syntax the flow runtime resolves. */
const getVariableReference = (variable = {}) => {
  const name = String(variable?.name || '').trim();
  if (!name) return '';
  return `${normalizeVariableScope(variable.scope)}.${name}`;
};

/** Purpose: recursively find variable placeholders inside config values. Why: API request editors can expose mappings for nested headers, body, and query templates. */
const collectTemplateVariables = (value, found = []) => {
  if (typeof value === 'string') {
    const pattern = /\{\{\{?\s*([a-zA-Z0-9_.-]+)\s*\}\}\}?/g;
    let match;
    while ((match = pattern.exec(value)) !== null) {
      const name = String(match[1] || '').trim();
      if (name && !found.includes(name)) found.push(name);
    }
    return found;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectTemplateVariables(item, found));
  } else if (value && typeof value === 'object') {
    Object.values(value).forEach((item) => collectTemplateVariables(item, found));
  }
  return found;
};

/** Purpose: collect unique template variables across an API request. Why: authors can map every request placeholder to a saved flow variable. */
const getRequestTemplateVariables = (request = {}) => collectTemplateVariables({
  url: request.url || '',
  headers: request.headers || {},
  query: request.query || {},
  body: request.body || {},
  auth: request.auth || {},
});

// const normalizeQuickReplyConfig = (config = {}) => {
//   const promptSource = String(config.prompt || config.message || '').trim();
//   const promptLines = promptSource
//     .split('\n')
//     .map((item) => item.trim())
//     .filter(Boolean);

//   const optionsFromPrompt = promptLines.length > 1 ? promptLines.slice(1) : [];
//   const options = Array.isArray(config.options) && config.options.length > 0
//     ? config.options
//     : optionsFromPrompt;

//   return {
//     prompt: promptLines[0] || promptSource || 'Choose an option below',
//     options: options.length > 0 ? options : ['Option 1', 'Option 2'],
//   };
// };


/** Purpose: normalize one quick-reply option's label/value/ID. Why: older and newer flow schemas need a common editor representation. */
const normalizeQuickReplyOption = (option, index = 0) => {
  if (typeof option === 'string') {
    return {
      title: option,
      text: option,
    };
  }

  if (option && typeof option === 'object') {
    const hasTitle = Object.prototype.hasOwnProperty.call(option, 'title');
    const hasText = Object.prototype.hasOwnProperty.call(option, 'text');

    return {
      ...option,
      title: String(
        hasTitle
          ? option.title ?? ''
          : option.label ?? option.text ?? `Option ${index + 1}`
      ),
      text: String(
        hasText
          ? option.text ?? ''
          : option.value ?? option.title ?? option.label ?? ''
      ),
      localizationKey: String(option.localizationKey || ''),
    };
  }

  return {
    title: `Option ${index + 1}`,
    text: `option_${index + 1}`,
  };
};

/** Purpose: normalize quick-reply config before editing. Why: choice labels and branch routing must remain aligned across saved schema versions. */
const normalizeQuickReplyConfig = (config = {}) => {
  const prompt = String(
    config.prompt || config.message || ''
  ).trim() || 'Choose an option below';

  const rawOptions =
    Array.isArray(config.options) &&
    config.options.length > 0
      ? config.options
      : [];

  const options =
    rawOptions.length > 0
      ? rawOptions.map((option, index) =>
          normalizeQuickReplyOption(option, index)
        )
      : [
          {
            title: 'Option 1',
            text: 'option_1',
          },
          {
            title: 'Option 2',
            text: 'option_2',
          },
        ];

  return {
    prompt,
    options,
  };
};



/** Purpose: immutably patch one list item. Why: React state updates rely on new references to refresh controlled node editors. */
const updateListEntry = (items, index, patch) => items.map((item, currentIndex) => (
  currentIndex === index ? { ...item, ...patch } : item
));

/** Purpose: edit structured JSON as text. Why: advanced node payloads need a direct editor while keeping invalid intermediate input editable. */
function JsonArea({ value, onChange, placeholder, minHeight = '110px' }) {
  return (
    <textarea
      value={JSON.stringify(value || {}, null, 2)}
      onChange={(event) => onChange(safeJsonParse(event.target.value, {}))}
      className={`${inputClass} resize-none font-mono text-xs`}
      style={{ minHeight }}
      placeholder={placeholder}
    />
  );
}

const DATABASE_FILTER_OPERATORS = [
  ['equals', 'Equals'], ['not_equals', 'Does not equal'], ['contains', 'Contains'],
  ['starts_with', 'Starts with'], ['greater_than', 'Greater than'],
  ['greater_than_or_equal', 'Greater than or equal'], ['less_than', 'Less than'],
  ['less_than_or_equal', 'Less than or equal'], ['in', 'Is one of'],
  ['exists', 'Has a value'], ['not_exists', 'Has no value'],
];

/** Purpose: choose a database filter/value from literals, variables, or table fields. Why: database nodes need explicit value-source metadata for runtime query construction. */
export function DatabaseValueSource({ value = '', onChange, variables = [], placeholder }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);

  const normalizedValue = String(value ?? '');
  /** Purpose: Build scoped variable choices and type metadata. Why: Pickers must insert valid runtime references. */
  const variableOptions = useMemo(() => {
    return (Array.isArray(variables) ? variables : [])
      .map((variable) => {
        const ref = getVariableReference(variable);
        if (!ref) return null;
        return {
          reference: ref,
          template: `{{${ref}}}`,
          name: variable?.name || ref,
          scope: normalizeVariableScope(variable?.scope),
          type: variable?.type || 'string',
        };
      })
      .filter(Boolean);
  }, [variables]);

  const selectedVariable = variableOptions.find((opt) => opt.template === normalizedValue) || null;
  const selectedVariableName = selectedVariable
    ? String(selectedVariable.name || selectedVariable.reference || '').replace(new RegExp(`^${selectedVariable.scope}\\.`, 'i'), '')
    : '';
  const isCustomValue = !selectedVariable;

  /** Purpose: Search variable names, references, and scopes. Why: Authors must locate entries in large registries. */
  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return variableOptions;
    return variableOptions.filter((opt) => (
      opt.reference.toLowerCase().includes(q)
      || opt.name.toLowerCase().includes(q)
      || opt.scope.toLowerCase().includes(q)
    ));
  }, [variableOptions, search]);

  useEffect(() => {
    if (!isOpen) return;
    // Purpose: close the source picker when focus moves outside it. Why: keeps the dropdown from obscuring the rest of the node editor.
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 50);
    } else {
      setSearch('');
    }
  }, [isOpen]);

  // Purpose: store a selected database value template. Why: value source and field reference must update as one valid config value.
  const handleSelect = (template) => {
    onChange(template);
    setIsOpen(false);
    setSearch('');
  };

  // Purpose: switch the value editor to a user-entered literal. Why: not every database query value comes from a field or variable.
  const handleSelectCustom = () => {
    onChange('');
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="space-y-2 relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        title={
          selectedVariable
            ? `${selectedVariable.reference}${selectedVariable.type ? ` (${selectedVariable.type})` : ''}`
            : 'Use a custom value'
        }
        className={[
          selectClass,
          'flex items-center justify-between text-left cursor-pointer transition-all duration-150',
          isOpen ? 'ring-2 ring-cyan-500/30 border-cyan-500 dark:border-cyan-400' : '',
        ].join(' ')}
      >
        <span className="truncate flex items-center gap-2 min-w-0">
          {selectedVariable ? (
            <>
              <span className={`px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md shrink-0 ${
                selectedVariable.scope === 'system'
                  ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                  : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300'
              }`}>
                {selectedVariable.scope}
              </span>
              <span className="font-mono text-xs font-semibold text-slate-800 dark:text-slate-100 truncate">
                {selectedVariableName}
              </span>
              {selectedVariable.type && (
                <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 shrink-0">
                  ({selectedVariable.type})
                </span>
              )}
            </>
          ) : (
            <span className="text-slate-600 dark:text-slate-300">
              Use a custom value
            </span>
          )}
        </span>
        <span className="flex items-center gap-1.5 shrink-0 text-slate-400 ml-2">
          {selectedVariable && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectCustom();
              }}
              className="p-0.5 hover:text-rose-500 dark:hover:text-rose-400 transition"
              title="Clear variable (use custom value)"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </span>
          )}
          <svg className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1.5 z-50 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-md shadow-xl dark:border-white/10 dark:bg-slate-900/95 overflow-hidden">
          <div className="p-2.5 border-b border-slate-100 dark:border-white/10 bg-slate-50/70 dark:bg-slate-800/50">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search variable name (e.g. session, phone, sender)..."
                className="w-full pl-8 pr-7 py-2 text-xs rounded-xl border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 outline-none placeholder:text-slate-400 focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20"
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setIsOpen(false);
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (filteredOptions.length > 0) {
                      handleSelect(filteredOptions[0].template);
                    }
                  }
                }}
              />
              <svg className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto p-1.5 space-y-0.5">
            {(!search || 'custom'.includes(search.toLowerCase()) || 'use a custom value'.includes(search.toLowerCase())) && (
              <button
                type="button"
                onClick={handleSelectCustom}
                className={`w-full text-left px-3 py-2 rounded-xl text-xs font-semibold flex items-center justify-between transition ${
                  isCustomValue
                    ? 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-800 dark:text-cyan-200'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                }`}
              >
                <span>Use a custom value</span>
                {isCustomValue && <span className="text-cyan-600 font-bold">✓</span>}
              </button>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const isSelected = selectedVariable?.template === opt.template;
                return (
                  <button
                    key={opt.reference}
                    type="button"
                    onClick={() => handleSelect(opt.template)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs flex items-center justify-between transition ${
                      isSelected
                        ? 'bg-cyan-50 dark:bg-cyan-950/40 text-cyan-800 dark:text-cyan-200 font-bold'
                        : 'text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/60'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`px-1.5 py-0.5 text-[9px] font-bold uppercase rounded-md shrink-0 ${
                        opt.scope === 'system'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300'
                          : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-500/20 dark:text-cyan-300'
                      }`}>
                        {opt.scope}
                      </span>
                      <span className="font-mono truncate">{opt.reference}</span>
                      {opt.type && (
                        <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0 font-normal">
                          ({opt.type})
                        </span>
                      )}
                    </div>
                    {isSelected && <span className="text-cyan-600 font-bold ml-2">✓</span>}
                  </button>
                );
              })
            ) : (
              <div className="py-4 px-3 text-center text-xs text-slate-400 dark:text-slate-500">
                No variables match &quot;{search}&quot;
              </div>
            )}
          </div>
        </div>
      )}

      {isCustomValue ? (
        <input
          value={normalizedValue}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
          placeholder={placeholder || 'Enter a value'}
        />
      ) : null}
    </div>
  );
}

/** Purpose: edit database filter predicates for the selected table. Why: each rule needs a coordinated field/operator/value selection. */
function DatabaseFilterEditor({ rules = [], table, variables, onChange, required = false }) {
  const fields = Array.isArray(table?.fields) ? table.fields : [];
  const normalizedRules = Array.isArray(rules) ? rules : [];
  // Purpose: patch one normalized predicate and report the entire list. Why: callers persist filters as an immutable array in node config.
  const updateRule = (index, patch) => onChange(normalizedRules.map((rule, currentIndex) => (
    currentIndex === index ? { ...rule, ...patch } : rule
  )));

  return <div className="space-y-3">
    {normalizedRules.map((rule, index) => <div key={rule.id || `filter-${index}`} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950/50">
      <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-slate-500">Filter {index + 1}</span><button type="button" onClick={() => onChange(normalizedRules.filter((_, currentIndex) => currentIndex !== index))} className="text-xs font-semibold text-rose-600">Remove</button></div>
      <select value={rule.field || ''} onChange={(event) => updateRule(index, { field: event.target.value })} className={selectClass}>
        <option value="">Select table field</option>
        {fields.map((field) => <option key={field.key} value={field.key}>{field.label || field.key} ({field.type || 'string'})</option>)}
      </select>
      <select value={rule.operator || 'equals'} onChange={(event) => updateRule(index, { operator: event.target.value })} className={selectClass}>
        {DATABASE_FILTER_OPERATORS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select>
      {!['exists', 'not_exists'].includes(rule.operator || 'equals') ? <DatabaseValueSource value={rule.value ?? ''} onChange={(value) => updateRule(index, { value })} variables={variables} placeholder={(rule.operator || 'equals') === 'in' ? 'active, pending, closed' : 'Enter the value to match'} /> : null}
    </div>)}
    <button type="button" disabled={!table || fields.length === 0} onClick={() => onChange([...normalizedRules, { id: `filter-${Date.now()}`, field: '', operator: 'equals', value: '' }])} className="w-full rounded-xl border border-cyan-200 px-4 py-2 text-sm font-semibold text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-cyan-400/20 dark:text-cyan-200">+ Add filter</button>
  </div>;
}

/** Purpose: edit a database field/value mapping. Why: insert/update operations require explicit column names paired with configured sources. */
function DatabaseValueEditor({ value = {}, table, variables, onChange, label }) {
  const fields = Array.isArray(table?.fields) ? table.fields : [];
  const entries = Object.entries(value || {});
  // Purpose: immutably update one column/value pair. Why: table writes must keep row mappings aligned while fields are edited.
  const updateEntry = (index, nextKey, nextValue) => {
    /** Purpose: Filter parameter entries by key. Why: The edited mapping must retain only the requested entries. */
    const nextEntries = entries.map(([key, itemValue], currentIndex) => currentIndex === index ? [nextKey, nextValue] : [key, itemValue]).filter(([key]) => key);
    onChange(Object.fromEntries(nextEntries));
  };

  return <div className="space-y-3">
    {entries.map(([key, itemValue], index) => <div key={`${key}-${index}`} className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-950/50">
      <div className="flex items-center justify-between"><span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label} {index + 1}</span><button type="button" onClick={() => onChange(Object.fromEntries(entries.filter((_, currentIndex) => currentIndex !== index)))} className="text-xs font-semibold text-rose-600">Remove</button></div>
      <select value={key} onChange={(event) => updateEntry(index, event.target.value, itemValue)} className={selectClass}>
        <option value="">Select table field</option>
        {fields.map((field) => <option key={field.key} value={field.key} disabled={field.key !== key && Object.prototype.hasOwnProperty.call(value || {}, field.key)}>{field.label || field.key} ({field.type || 'string'})</option>)}
      </select>
      <DatabaseValueSource value={itemValue ?? ''} onChange={(nextValue) => updateEntry(index, key, nextValue)} variables={variables} placeholder="Enter the value to save" />
    </div>)}
    <button type="button" disabled={!table || fields.length === 0 || fields.every((field) => Object.prototype.hasOwnProperty.call(value || {}, field.key))} onClick={() => { /** Purpose: Find a schema field not already configured. Why: Adding fields should avoid immediate duplicates. */ const availableField = fields.find((field) => !Object.prototype.hasOwnProperty.call(value || {}, field.key)); if (availableField) onChange({ ...(value || {}), [availableField.key]: '' }); }} className="w-full rounded-xl border border-cyan-200 px-4 py-2 text-sm font-semibold text-cyan-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-cyan-400/20 dark:text-cyan-200">+ Add value</button>
  </div>;
}

/** Purpose: render variable-aware controls for node config fields. Why: users should select known flow variables instead of hand-typing fragile references. */
function VariableField({
  value = '',
  onChange,
  variables = [],
  placeholder = 'variable_name',
}) {
  const normalizedValue = String(value || '').trim();
  const options = Array.isArray(variables)
    ? variables.filter((item) => item.scope !== 'system').map((item) => getVariableReference(item)).filter(Boolean)
    : [];
  const uniqueOptions = [...new Set(options)];
  const usesCustomValue = normalizedValue && !uniqueOptions.includes(normalizedValue);
  const selectedValue = usesCustomValue ? '__custom__' : normalizedValue;

  return (
    <div className="space-y-2">
      <select
        value={selectedValue || ''}
        onChange={(event) => {
          if (event.target.value === '__custom__') {
            onChange(normalizedValue && usesCustomValue ? normalizedValue : '');
            return;
          }
          onChange(event.target.value);
        }}
        className={selectClass}
      >
        <option value="">Select variable</option>
        {uniqueOptions.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
        <option value="__custom__">Custom variable name</option>
      </select>
      {(usesCustomValue || !normalizedValue) ? (
        <input
          value={normalizedValue}
          onChange={(event) => onChange(event.target.value)}
          className={inputClass}
          placeholder={placeholder}
        />
      ) : null}
    </div>
  );
}

/** Purpose: edit text with variable-token suggestions. Why: messages and API templates need discoverable variable insertion without changing runtime token syntax. */
export function TemplateTextarea({
  value = '',
  onChange,
  variables = [],
  className = '',
  placeholder,
  title = 'Prompt Editor',
  subtitle = 'Full-screen editor with variable autocomplete and starter templates',
  allowFullscreen = false,
}) {
  const inputRef = React.useRef(null);
  const [templateQuery, setTemplateQuery] = React.useState(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const normalizedVariables = Array.isArray(variables) ? variables : [];
  /** Purpose: Build unique scoped template references. Why: Repeated registry values must not duplicate autocomplete choices. */
  const templateOptions = React.useMemo(() => {
    const seen = new Set();
    return normalizedVariables.reduce((options, variable) => {
      const name = String(variable?.name || '').trim();
      const scope = normalizeVariableScope(variable?.scope);
      const reference = `${scope}.${name}`;
      if (!name || seen.has(reference)) return options;
      seen.add(reference);
      options.push({
        value: reference,
        label: reference,
        detail: `${scope} variable`,
      });
      return options;
    }, []);
  }, [normalizedVariables]);

  // Purpose: update the variable suggestion menu for the current caret. Why: suggestions should track the token being typed rather than the whole textarea.
  const updateSuggestion = (rawValue, cursor) => {
    const beforeCursor = rawValue.slice(0, cursor);
    const match = beforeCursor.match(/\{{1,3}\s*([a-zA-Z0-9_.-]*)$/);
    setTemplateQuery(match ? match[1] : null);
  };

  // Purpose: insert a selected variable token at the active caret. Why: preserves surrounding text and avoids replacing the entire message template.
  const insertTemplate = (reference) => {
    const element = inputRef.current;
    const currentValue = String(value || '');
    const hasSelection = typeof element?.selectionStart === 'number' && typeof element?.selectionEnd === 'number';
    const hasActiveSelection = hasSelection && (element.selectionStart !== 0 || element.selectionEnd !== 0);
    const hasFocus = typeof document !== 'undefined' && document.activeElement === element;
    const cursor = (hasActiveSelection || hasFocus) ? element.selectionStart : currentValue.length;
    const match = currentValue.slice(0, cursor).match(/\{{1,3}\s*[a-zA-Z0-9_.-]*$/);
    const start = match ? cursor - match[0].length : cursor;
    const end = (hasActiveSelection || hasFocus) ? element.selectionEnd : cursor;
    const nextValue = `${currentValue.slice(0, start)}{{${reference}}}${currentValue.slice(end)}`;
    onChange(nextValue);
    setTemplateQuery(null);
    window.requestAnimationFrame(() => {
      const nextCursor = start + reference.length + 4;
      element?.focus();
      element?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const matchingOptions = templateQuery === null
    ? []
    : templateOptions.filter((option) => option.label.toLowerCase().includes(templateQuery.toLowerCase())).slice(0, 8);

  return (
    <div className="relative group">
      <div className="relative">
        <textarea
          ref={inputRef}
          value={value || ''}
          onChange={(event) => {
            onChange(event.target.value);
            updateSuggestion(event.target.value, event.target.selectionStart ?? event.target.value.length);
          }}
          onClick={(event) => updateSuggestion(event.currentTarget.value, event.currentTarget.selectionStart ?? event.currentTarget.value.length)}
          onKeyUp={(event) => updateSuggestion(event.currentTarget.value, event.currentTarget.selectionStart ?? event.currentTarget.value.length)}
          className={`${className} ${allowFullscreen ? 'pr-20' : 'pr-12'}`}
          placeholder={placeholder}
        />
        <div className="absolute right-2 top-2 z-10 flex items-center gap-1">
          {/* Integrated {x} Variable Selector */}
          <div className="relative" title="Insert variable">
            <button
              type="button"
              className="inline-flex items-center gap-0.5 rounded-md border border-slate-200/90 bg-white/95 px-1.5 py-0.5 text-[11px] font-mono font-bold text-slate-600 shadow-xs hover:border-cyan-400 hover:bg-cyan-50/50 hover:text-cyan-700 dark:border-white/10 dark:bg-slate-900/90 dark:text-slate-300 dark:hover:border-cyan-500/40 dark:hover:bg-slate-800 dark:hover:text-cyan-300 transition-all cursor-pointer"
              tabIndex={-1}
            >
              <span>{'{x}'}</span>
            </button>
            <select
              aria-label="Insert variable"
              value=""
              onChange={(event) => {
                if (event.target.value) {
                  insertTemplate(event.target.value);
                  event.target.value = '';
                }
              }}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              title="Insert variable"
            >
              <option value="">Insert variable...</option>
              {['variables', 'journey', 'session', 'global', 'system'].map((scope) => {
                /** Purpose: Restrict suggestions to a displayed scope. Why: Grouped menus must keep scope boundaries clear. */
                const scoped = templateOptions.filter((option) => option.value.startsWith(`${scope}.`));
                if (!scoped.length) return null;
                return (
                  <optgroup key={scope} label={scope}>
                    {scoped.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
          </div>

          {allowFullscreen && (
            <button
              type="button"
              onClick={() => setIsFullscreen(true)}
              className="rounded-md border border-slate-200/90 bg-white/95 p-1 text-slate-400 shadow-xs hover:border-cyan-300 hover:bg-cyan-50/50 hover:text-cyan-700 dark:border-white/10 dark:bg-slate-900/90 dark:hover:bg-slate-800 dark:hover:text-cyan-300 transition cursor-pointer"
              title="Open Fullscreen Editor"
              aria-label="Open Fullscreen Editor"
            >
              <OpenInFullRoundedIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {templateQuery !== null ? (
        <div className="absolute left-2 right-2 top-full z-30 mt-2 overflow-hidden rounded-xl border border-cyan-200 bg-white shadow-2xl dark:border-cyan-400/30 dark:bg-slate-950">
          {matchingOptions.length > 0 ? matchingOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => insertTemplate(option.value)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-slate-800 transition hover:bg-cyan-50 dark:text-slate-100 dark:hover:bg-cyan-500/15"
            >
              <code className="text-cyan-700 dark:text-cyan-200">{`{${option.label}}`}</code>
              <span className="text-xs text-slate-500">{option.detail}</span>
            </button>
          )) : (
            <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">No saved variables match this name.</div>
          )}
        </div>
      ) : null}

      {allowFullscreen && (
        <PromptEditorModal
          isOpen={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          value={value}
          onChange={onChange}
          title={title}
          subtitle={subtitle}
          variables={variables}
        />
      )}
    </div>
  );
}

/** Purpose: map request placeholders to flow variables. Why: dynamic headers, URL parts, and payload values need explicit runtime substitutions. */
function RequestVariableMappingEditor({ placeholders = [], mappings = {}, variables = [], onChange }) {
  if (placeholders.length === 0) return null;

  return (
    <div className="rounded-3xl border border-cyan-200 bg-cyan-50 p-4 dark:border-cyan-400/20 dark:bg-cyan-500/10">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-200">API input mapping</div>
      <div className="mt-3 space-y-3">
        {placeholders.map((placeholder) => (
          <div key={placeholder} className="rounded-xl border border-slate-200 bg-white p-3 dark:border-white/10 dark:bg-slate-950/60">
            <div className="mb-2 font-mono text-xs text-cyan-700 dark:text-cyan-200">{`{{${placeholder}}}`}</div>
            <VariableField
              value={mappings?.[placeholder] || ''}
              onChange={(value) => onChange({ ...(mappings || {}), [placeholder]: value })}
              variables={variables}
              placeholder="Select the flow variable to send"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

/** Purpose: edit a tag array using comma-separated text. Why: keep the UI compact while saving a normalized list for routing rules. */
function CsvTagField({ value = [], onChange, placeholder = 'ticket, urgent, priority_support' }) {
  const textValue = Array.isArray(value) ? value.join(', ') : String(value || '');

  return (
    <input
      value={textValue}
      onChange={(event) => {
        const nextValue = event.target.value
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean);
        onChange(nextValue);
      }}
      className={inputClass}
      placeholder={placeholder}
    />
  );
}

// function OptionsEditor({
//   value = [],
//   onChange,
//   label = 'Options',
//   helpText = 'One choice per line.',
//   maxItems = null,
//   maxItemsHelpText = '',
// }) {
//   const options = value.length > 0 ? value : ['Option 1', 'Option 2'];
//   const atLimit = Number.isInteger(maxItems) && options.length >= maxItems;

//   return (
//     <div className="space-y-3">
//       <div>
//         <div className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
//         <div className="text-xs leading-5 text-slate-500">{helpText}</div>
//         {atLimit && maxItemsHelpText ? (
//           <div className="mt-2 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-amber-100">
//             {maxItemsHelpText}
//           </div>
//         ) : null}
//       </div>

//       <div className="flex flex-wrap gap-2">
//         {options.map((option, index) => (
//           <button
//             key={`${option}-${index}`}
//             type="button"
//             onClick={() => onChange(options.filter((_, currentIndex) => currentIndex !== index))}
//             className="group inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-2 text-sm font-medium text-cyan-700 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:border-rose-400/30 dark:hover:bg-rose-500/10 dark:hover:text-rose-100"
//             title="Remove option"
//           >
//             <span>{option || `Option ${index + 1}`}</span>
//             <span className="text-xs text-cyan-300 transition group-hover:text-rose-200">x</span>
//           </button>
//         ))}
//       </div>

//       <div className="space-y-2">
//         {options.map((option, index) => (
//           <input
//             key={`input-${index}`}
//             value={option}
//             onChange={(event) => onChange(options.map((item, currentIndex) => (
//               currentIndex === index ? event.target.value : item
//             )))}
//             className={inputClass}
//             placeholder={`Option ${index + 1}`}
//           />
//         ))}
//       </div>

//       <button
//         type="button"
//         onClick={() => {
//           if (atLimit) return;
//           onChange([...(options || []), `Option ${options.length + 1}`]);
//         }}
//         disabled={atLimit}
//         className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:border-cyan-300 hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
//       >
//         {atLimit ? 'Button limit reached' : 'Add button'}
//       </button>
//     </div>
//   );
// }
/** Purpose: edit a bounded set of quick-reply options. Why: node branches depend on stable editable choices rather than free-form message text. */
export function OptionsEditor({
  value = [],
  onChange,
  onReorder,
  label = 'Options',
  helpText = '',
  maxItems = null,
  maxItemsHelpText = '',
  localizationKeys = [],
}) {
  const draggedIndex = useRef(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [orderRevision, setOrderRevision] = useState(0);
  const moveOption = (from, to) => {
    if (!onReorder || from === null || from === to || to < 0 || to >= options.length) return;
    onReorder(from, to);
    // Reset positional field UI (such as an open key search) after moving its underlying choice.
    setOrderRevision((revision) => revision + 1);
  };
  const options =
    Array.isArray(value) && value.length > 0
      ? value.map((option, index) =>
          normalizeQuickReplyOption(option, index)
        )
      : [
          {
            title: 'Option 1',
            text: 'option_1',
          },
          {
            title: 'Option 2',
            text: 'option_2',
          },
        ];

  const atLimit =
    Number.isInteger(maxItems) &&
    options.length >= maxItems;

  // Purpose: update one quick-reply option while retaining its existing fields. Why: editing a label must not discard IDs or routing metadata.
  const updateOption = (index, patch) => {
    onChange(
      options.map((option, currentIndex) =>
        currentIndex === index
          ? {
              ...option,
              ...patch,
            }
          : option
      )
    );
  };

  // Purpose: remove a choice from the options list. Why: branch configuration must reflect the choices users can actually select.
  const removeOption = (index) => {
    onChange(
      options.filter(
        (_, currentIndex) =>
          currentIndex !== index
      )
    );
  };

  // Purpose: append a default quick-reply choice within the limit. Why: authors need to grow the menu without constructing option objects manually.
  const addOption = () => {
    if (atLimit) return;

    const nextNumber = options.length + 1;

    onChange([
      ...options,
      {
        title: `Option ${nextNumber}`,
        text: `option_${nextNumber}`,
      },
    ]);
  };

  return (
    <div className="space-y-4">
      {/* HEADER */}
      <div>
        <div className="mb-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          {label}
        </div>

        {helpText ? (
          <div className="text-xs leading-5 text-slate-500">
            {helpText}
          </div>
        ) : null}

        {atLimit && maxItemsHelpText ? (
          <div className="mt-2 rounded-2xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs leading-5 text-amber-100">
            {maxItemsHelpText}
          </div>
        ) : null}
      </div>

      {/* SMALL OPTION CHIPS */}
      <div className="flex flex-wrap gap-2">
        {options.map((option, index) => (
          <div
            key={`chip-${index}`}
            className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-xs font-medium text-cyan-700 dark:text-cyan-100"
          >
            <span>
              {option.localizationKey ? `🌐 ${option.localizationKey}` : option.title ||
                `Option ${index + 1}`}
            </span>
          </div>
        ))}
      </div>

      {/* OPTION CARDS */}
      <div className="space-y-3">
        {options.map((option, index) => (
          <div
            key={`quick-reply-option-${orderRevision}-${index}`}
            onDragOver={(event) => {
              if (draggedIndex.current === null) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              setDropIndex(index);
            }}
            onDrop={(event) => {
              if (draggedIndex.current === null) return;
              event.preventDefault();
              event.stopPropagation();
              moveOption(draggedIndex.current, index);
              draggedIndex.current = null;
              setDropIndex(null);
            }}
            className={`rounded-2xl border bg-slate-50/80 p-3 shadow-sm dark:bg-slate-950/70 ${dropIndex === index ? 'border-cyan-400 ring-2 ring-cyan-400/30' : 'border-slate-200 dark:border-white/10'}`}
          >
            {/* OPTION HEADER */}
            <div className="mb-3 flex items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                {onReorder ? <button
                  type="button"
                  draggable
                  aria-label={`Drag button ${index + 1} to reorder`}
                  title="Drag to reorder"
                  onDragStart={(event) => {
                    draggedIndex.current = index;
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('application/x-quick-reply-order', String(index));
                    event.stopPropagation();
                  }}
                  onDragEnd={() => { draggedIndex.current = null; setDropIndex(null); }}
                  className="nodrag nopan cursor-grab rounded-md px-1 py-1 text-lg leading-none text-slate-400 hover:bg-cyan-50 hover:text-cyan-700 active:cursor-grabbing dark:hover:bg-white/5"
                >⠿</button> : null}
                <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-cyan-500 text-[11px] font-bold text-white">
                  {index + 1}
                </div>

                <div className="min-w-0 truncate text-xs font-semibold text-slate-700 dark:text-slate-200" title={option.localizationKey || `Option ${index + 1}`}>
                  {option.localizationKey ? `🌐 ${option.localizationKey}` : `Option ${index + 1}`}
                </div>
              </div>

              <div className="flex shrink-0 items-center gap-1">
              {onReorder ? <>
                <button type="button" aria-label={`Move button ${index + 1} up`} title="Move up" disabled={index === 0}
                  onClick={() => moveOption(index, index - 1)}
                  className="rounded-md px-1.5 py-1 text-slate-500 hover:bg-cyan-50 disabled:opacity-30 dark:hover:bg-white/5">↑</button>
                <button type="button" aria-label={`Move button ${index + 1} down`} title="Move down" disabled={index === options.length - 1}
                  onClick={() => moveOption(index, index + 1)}
                  className="rounded-md px-1.5 py-1 text-slate-500 hover:bg-cyan-50 disabled:opacity-30 dark:hover:bg-white/5">↓</button>
              </> : null}
              <button
                type="button"
                onClick={() => removeOption(index)}
                className="rounded-lg border border-rose-400/20 bg-rose-500/10 px-2 py-1 text-[10px] font-semibold text-rose-600 transition hover:bg-rose-500/20 dark:text-rose-200"
              >
                Remove
              </button>
              </div>
            </div>

            <div className="space-y-3">
              {/* TITLE */}
              <Field
                label="Title"
        
              >
                <QuickReplyTitle
                  key={`${index}-${option.localizationKey ? 'mapped' : 'manual'}`}
                  option={option}
                  localizationKeys={localizationKeys}
                  onChange={(patch) => updateOption(index, patch)}
                  inputClass={inputClass}
                  selectClass={selectClass}
                />
              </Field>

              {/* TEXT */}
              <Field
                label="Text"
           
              >
                <input
                  value={option.text || ''}
                  onChange={(event) =>
                    updateOption(index, {
                      text: event.target.value,
                    })
                  }
                  className={inputClass}
                  placeholder="check_policy"
                />
              </Field>
            </div>

            {/* PREVIEW
            <div className="mt-3 rounded-xl border border-cyan-400/10 bg-cyan-500/5 px-3 py-2">
              <div className="text-[10px] uppercase tracking-[0.16em] text-slate-500">
                Preview
              </div>

              <div className="mt-1 text-sm font-semibold text-cyan-700 dark:text-cyan-200">
                {option.title ||
                  `Option ${index + 1}`}
              </div>

              <div className="mt-0.5 text-[11px] text-slate-500">
                sends:{' '}
                <span className="font-mono">
                  {option.text || '—'}
                </span>
              </div>
            </div> */}
          </div>
        ))}
      </div>

      {/* ADD BUTTON */}
      <button
        type="button"
        onClick={addOption}
        disabled={atLimit}
        className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:border-cyan-300 hover:bg-cyan-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
      >
        {atLimit
          ? 'Button limit reached'
          : '+ Add button'}
      </button>
    </div>
  );
}
/** Purpose: edit selectable list-message items. Why: WhatsApp/list nodes require structured rows rather than a single message string. */
function ListItemsEditor({ items = [], onChange, maxItems = 10 }) {
  const normalizedItems = items.length > 0 ? items : [{ label: '', value: '', description: '' }];
  const atLimit = normalizedItems.length >= maxItems;

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">List items</div>
      {normalizedItems.map((item, index) => (
        <div key={`${item.value || item.label || 'item'}-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-slate-950/70">
          <div className="grid gap-3">
            <input
              value={item.label || ''}
              onChange={(event) => onChange(updateListEntry(normalizedItems, index, { label: event.target.value }))}
              className={inputClass}
              placeholder="Label"
            />
            <input
              value={item.value || ''}
              onChange={(event) => onChange(updateListEntry(normalizedItems, index, { value: event.target.value }))}
              className={inputClass}
              placeholder="Value"
            />
            <textarea
              value={item.description || ''}
              onChange={(event) => onChange(updateListEntry(normalizedItems, index, { description: event.target.value }))}
              className={`${inputClass} min-h-[84px] resize-none`}
              placeholder="Short description"
            />
            <button
              type="button"
              onClick={() => onChange(normalizedItems.filter((_, currentIndex) => currentIndex !== index))}
              className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
            >
              Remove item
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...(normalizedItems || []), { label: '', value: '', description: '' }])}
        disabled={atLimit}
        className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
      >
        {atLimit ? '10 item limit reached' : 'Add item'}
      </button>
    </div>
  );
}

/** Purpose: configure output-to-variable assignments. Why: node results must be saved under known variables for later flow steps. */
function VariableAssignmentsEditor({ items = [], onChange, variables = [] }) {
  const normalizedItems = items.length > 0 ? items : [{ targetVariable: '', value: '', valueType: 'text', sourceVariable: '' }];

  // Purpose: update one assignment row without mutating editor state. Why: each row independently maps a result path to a variable.
  const updateItem = (index, patch) => onChange(normalizedItems.map((item, currentIndex) => (
    currentIndex === index ? { ...item, ...patch } : item
  )));

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Variable assignments</div>
      {normalizedItems.map((item, index) => (
        <div key={`assignment-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-slate-950/70">
          <div className="grid gap-3">
            <VariableField
              value={item.targetVariable || ''}
              onChange={(value) => updateItem(index, { targetVariable: value })}
              variables={variables}
              placeholder="target_variable"
            />
            <select
              value={item.valueType || 'text'}
              onChange={(event) => updateItem(index, { valueType: event.target.value })}
              className={selectClass}
            >
              <option value="text">Text / template value</option>
              <option value="variable">Copy from variable</option>
              <option value="json">JSON value</option>
            </select>

            {item.valueType === 'variable' ? (
              <VariableField
                value={item.sourceVariable || ''}
                onChange={(value) => updateItem(index, { sourceVariable: value })}
                variables={variables}
                placeholder="customer_name"
              />
            ) : item.valueType === 'json' ? (
              <textarea
                value={JSON.stringify(item.value ?? {}, null, 2)}
                onChange={(event) => updateItem(index, { value: safeJsonParse(event.target.value, {}) })}
                className={`${inputClass} min-h-[100px] resize-none font-mono text-xs`}
                placeholder='{"status":"active"}'
              />
            ) : (
              <input
                value={item.value || ''}
                onChange={(event) => updateItem(index, { value: event.target.value })}
                className={inputClass}
                placeholder="{{user_input}}"
              />
            )}

            <button
              type="button"
              onClick={() => onChange(normalizedItems.filter((_, currentIndex) => currentIndex !== index))}
              className="justify-self-start rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
            >
              Remove assignment
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...(normalizedItems || []), { targetVariable: '', value: '', valueType: 'text', sourceVariable: '' }])}
        className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
      >
        Add assignment
      </button>
    </div>
  );
}

/** Purpose: configure API response path mappings. Why: response data needs explicit extraction into flow variables before downstream nodes can use it. */
function ResponseMappingEditor({ items = [], onChange, variables = [] }) {
  const normalizedItems = items.length > 0 ? items : [{ sourcePath: '', targetVariable: '' }];

  // Purpose: update one response mapping immutably. Why: mappings are persisted as a full array in the node config.
  const updateItem = (index, patch) => onChange(normalizedItems.map((item, currentIndex) => (
    currentIndex === index ? { ...item, ...patch } : item
  )));

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Response mapping</div>
      {normalizedItems.map((item, index) => (
        <div key={`mapping-${index}`} className="rounded-2xl border border-slate-200 bg-slate-50/80 p-3 dark:border-white/10 dark:bg-slate-950/70">
          <div className="grid gap-3">
            <input
              value={item.sourcePath || ''}
              onChange={(event) => updateItem(index, { sourcePath: event.target.value })}
              className={inputClass}
              placeholder="customer.name or leave blank for full response"
            />
            <VariableField
              value={item.targetVariable || ''}
              onChange={(value) => updateItem(index, { targetVariable: value })}
              variables={variables}
              placeholder="customer_name"
            />
            <button
              type="button"
              onClick={() => onChange(normalizedItems.filter((_, currentIndex) => currentIndex !== index))}
              className="justify-self-start rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
            >
              Remove mapping
            </button>
          </div>
        </div>
      ))}

      <button
        type="button"
        onClick={() => onChange([...(normalizedItems || []), { sourcePath: '', targetVariable: '' }])}
        className="rounded-2xl border border-cyan-200 bg-cyan-50 px-4 py-2 text-sm font-semibold text-cyan-700 transition hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
      >
        Add mapping
      </button>
    </div>
  );
}

// function QuickReplyRoutingEditor({
//   rows = [],
//   fallbackEdge = null,
//   nextNodeOptions = [],
//   onEdgeChange,
//   onCreateEdge,
//   onDeleteEdge,
//   sourceNodeId,
// }) {
//   if (!rows.length) return null;

//   return (
//     <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-slate-950/70">
//       <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Button routing</div>
//       <div className="space-y-3">
//         {[...rows, { option: 'Fallback', edge: fallbackEdge, isFallback: true, sourceHandle: 'quick-reply-fallback' }].map(({ option, edge, isFallback = false, sourceHandle = '' }) => (
//           <div key={option} className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/5">
//             <div className={`text-sm font-medium ${isFallback ? 'text-rose-600 dark:text-rose-300' : 'text-slate-700 dark:text-slate-200'}`}>{option}</div>
//             <select
//               value={edge?.target || ''}
//               onChange={(event) => {
//                 const target = event.target.value;
//                 if (!target) return;
//                 if (edge) {
//                   onEdgeChange?.(edge.id, {
//                     target,
//                     label: isFallback ? 'Fallback' : option,
//                     data: {
//                       ...(edge.data || {}),
//                       condition: isFallback ? 'fallback' : option,
//                     },
//                   });
//                   return;
//                 }

//                 onCreateEdge?.(sourceNodeId, target, {
//                   label: isFallback ? 'Fallback' : option,
//                   sourceHandle,
//                   data: {
//                     condition: isFallback ? 'fallback' : option,
//                   },
//                 });
//               }}
//               className={selectClass}
//             >
//               <option value="">Select target node</option>
//               {nextNodeOptions.map((nextNode) => (
//                 <option key={`${option}-${nextNode.id}`} value={nextNode.id}>
//                   {nextNode.label}
//                 </option>
//               ))}
//             </select>
//             {edge ? (
//               <button
//                 type="button"
//                 onClick={() => onDeleteEdge?.(edge.id)}
//                 className="justify-self-start rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
//               >
//                 Remove route
//               </button>
//             ) : null}
//           </div>
//         ))}
//       </div>
//     </div>
//   );
// }
/** Purpose: connect each quick-reply option to a routing condition. Why: selecting a button should continue along the corresponding canvas branch. */
function QuickReplyRoutingEditor({
  rows = [],
  fallbackEdge = null,
  nextNodeOptions = [],
  onEdgeChange,
  onCreateEdge,
  onDeleteEdge,
  sourceNodeId,
}) {
  if (!rows.length) return null;

  const routingRows = [
    ...rows,
    {
      option: 'Fallback',
      value: 'fallback',
      edge: fallbackEdge,
      isFallback: true,
      sourceHandle:
        'quick-reply-fallback',
    },
  ];

  return (
    <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-slate-950/70">
      <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
        Button routing
      </div>

      <div className="space-y-3">
        {routingRows.map(
          ({
            option,
            value,
            edge,
            isFallback = false,
            sourceHandle = '',
          }) => {
            const routeValue = isFallback
              ? 'fallback'
              : String(
                  value || option || ''
                ).trim();

            return (
              <div
                key={`${sourceHandle}-${option}`}
                className="grid gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm dark:border-white/10 dark:bg-white/5"
              >
                <div
                  className={`text-sm font-medium ${
                    isFallback
                      ? 'text-rose-600 dark:text-rose-300'
                      : 'text-slate-700 dark:text-slate-200'
                  }`}
                >
                  {option}
                </div>

                {!isFallback ? (
                  <div className="text-[10px] text-slate-500">
                    sends:{' '}
                    <span className="font-mono">
                      {routeValue || '—'}
                    </span>
                  </div>
                ) : null}

                <select
                  value={edge?.target || ''}
                  onChange={(event) => {
                    const target =
                      event.target.value;

                    if (!target) return;

                    if (edge) {
                      onEdgeChange?.(
                        edge.id,
                        {
                          target,

                          // Line par TITLE
                          label: isFallback
                            ? 'Fallback'
                            : option,

                          data: {
                            ...(edge.data ||
                              {}),

                            // Actual routing TEXT
                            condition:
                              routeValue,
                          },
                        }
                      );

                      return;
                    }

                    onCreateEdge?.(
                      sourceNodeId,
                      target,
                      {
                        label: isFallback
                          ? 'Fallback'
                          : option,

                        sourceHandle,

                        data: {
                          condition:
                            routeValue,
                        },
                      }
                    );
                  }}
                  className={selectClass}
                >
                  <option value="">
                    Select target node
                  </option>

                  {nextNodeOptions.map(
                    (nextNode) => (
                      <option
                        key={`${sourceHandle}-${nextNode.id}`}
                        value={nextNode.id}
                      >
                        {nextNode.label}
                      </option>
                    )
                  )}
                </select>

                {edge ? (
                  <button
                    type="button"
                    onClick={() =>
                      onDeleteEdge?.(
                        edge.id
                      )
                    }
                    className="justify-self-start rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
                  >
                    Remove route
                  </button>
                ) : null}
              </div>
            );
          }
        )}
      </div>
    </div>
  );
}
/** Purpose: render editable configuration for the selected flow node. Why: centralizes node-specific controls and routes changes back into graph state. */
export default function FlowPropertiesPanel({
  collapsed,
  onToggle,
  activeTab = 'inspector',
  onTabChange,
  flowName,
  flowDescription,
  onFlowChange,
  variables: flowVariables = [],
  onVariablesChange,
  onAddVariable,
  onUpdateVariable,
  onDeleteVariable,
  selectedNode,
  selectedEdge,
  nodes = [],
  edges = [],
  onNodeChange,
  onReorderQuickReplyOptions,
  onEdgeChange,
  onDeleteNode,
  onDeleteEdge,
  onCreateEdge,
  onClearSelection,
  onClearEdgeSelection,
  onCopyJson,
  onExportJson,
  onFitView,
  onRefresh,
  saving,
  loading,
  isNewFlow = false,
  customTables = [],
  savedApis = [],
  savedFunctions = [],
  availableFlows = [],
  currentFlowId = '',
  currentBotId = '',
  currentEnvironment = 'dev',
  debuggerProps = {},
  fullscreen = false,
  onToggleFullscreen,
  onSaveFlow,
  isReadOnly = false,
  workspaceSettings = false,
}) {
  const registryContext = `${currentBotId}:${currentEnvironment}`;
  const registryContextRef = React.useRef(registryContext);
  registryContextRef.current = registryContext;
  const [registry, setRegistry] = useState({ globalVariables: [], systemVariables: [], canManageGlobals: false });
  const [registryLoading, setRegistryLoading] = useState(false);
  const [registryError, setRegistryError] = useState('');
  const [registryRetry, setRegistryRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setRegistry({ globalVariables: [], systemVariables: [], canManageGlobals: false });
    setRegistryError('');
    if (!currentBotId) return () => { active = false; };
    setRegistryLoading(true);
    axios.get(`/api/v1/environments/${currentBotId}/${currentEnvironment}/variables`)
      .then(({ data }) => { if (active) setRegistry(data); })
      .catch(() => { if (active) setRegistryError('Could not load global and system variables.'); })
      .finally(() => { if (active) setRegistryLoading(false); });
    return () => { active = false; };
  }, [currentBotId, currentEnvironment, registryRetry]);
  /** Purpose: Combine flow, global, and system variable registries. Why: Source metadata distinguishes editable values from system-owned values. */
  const variables = React.useMemo(() => [
    ...flowVariables.map((item, index) => ({ ...item, _source: 'flow', _index: index })),
    ...(registry.globalVariables || []).map((item) => ({ ...item, scope: 'global', _source: 'environment' })),
    ...(registry.systemVariables || []).map((item) => ({ ...item, scope: 'system', _source: 'system' })),
  ], [flowVariables, registry]);
  // Purpose: persist edits to the shared global-variable registry. Why: global variables are managed separately from the current flow's local definitions.
  const saveGlobalVariables = async (items) => {
    /** Purpose: Project global-variable fields for persistence. Why: UI-only registry metadata must not enter the API payload. */
    const globalVariables = items.map(({ name, type, defaultValue, description }) => ({ name, type, defaultValue, description }));
    const { data } = await axios.put(`/api/v1/environments/${currentBotId}/${currentEnvironment}`, { config: { globalVariables }, expectedGlobalVariables: registry.globalVariables || [] });
    if (registryContextRef.current === registryContext) {
      setRegistry((previous) => ({ ...previous, globalVariables: data.environment?.config?.globalVariables || globalVariables }));
    }
  };
  const [translationLanguage, setTranslationLanguage] = useState('hi');
  const [localizationKeys, setLocalizationKeys] = useState([]);
  useEffect(() => {
    let active = true;
    if (!currentBotId) { setLocalizationKeys([]); return () => { active = false; }; }
    axios.get('/api/v1/localizations', { params: { botId: currentBotId, env: currentEnvironment } })
      .then((response) => { if (active) setLocalizationKeys((response.data.localization?.entries || []).map((entry) => entry.key).filter(Boolean)); })
      .catch(() => { if (active) setLocalizationKeys([]); });
    return () => { active = false; };
  }, [currentBotId, currentEnvironment]);
  const translationLanguageLabel = CONVERSATION_LANGUAGES.find((item) => item.code === translationLanguage)?.label || translationLanguage;
  const selectedLibraryKey = selectedNode?.data?.libraryKey || '';
  const selectedConfig = selectedNode?.data?.config || {};
  const normalizedQuickReply = normalizeQuickReplyConfig(selectedConfig);
  const isMediaNode = MEDIA_NODE_KEYS.has(selectedLibraryKey);
  const isQuickReplyNode = QUICK_REPLY_KEYS.has(selectedLibraryKey)
    || /quick\s*repl/i.test(selectedNode?.data?.title || '')
    || selectedConfig.variable === 'quick_reply'
    || selectedConfig.validation === 'quick_reply'
    || (Array.isArray(selectedConfig.options) && selectedConfig.options.length > 0);
  const isListNode = LIST_KEYS.has(selectedLibraryKey);
  const isDynamicNode = selectedLibraryKey === 'dynamic_chat'
    || selectedNode?.type === 'genai_prompt'
    || selectedConfig?.uiNodeKey === 'dynamic_chat';
  const visibleTab = isDynamicNode ? 'inspector' : activeTab;
  useEffect(() => {
    if (!fullscreen) return undefined;
    /** Purpose: Exit the expanded settings panel on Escape. Why: Keyboard dismissal must match the fullscreen control. */
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onToggleFullscreen?.();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [fullscreen, onToggleFullscreen]);
  const isSetLanguageNode = selectedLibraryKey === 'set_language';
  const isDetectLanguageNode = selectedLibraryKey === 'detect_language' || selectedConfig?.uiNodeKey === 'detect_language';
  const isAbSplitNode = selectedLibraryKey === 'ab_split' || selectedConfig?.uiNodeKey === 'ab_split';
  const isJumpNode = selectedLibraryKey === 'execute_flow' || selectedLibraryKey === 'switch_flow';
  const isFunctionNode = selectedLibraryKey === 'function' || selectedNode?.type === 'custom_function';
  const isDatabaseNode = selectedLibraryKey === 'database';
  const selectedCustomTable = customTables.find((table) => table.tableId === selectedNode?.data?.config?.customStorage?.tableId) || null;
  const isVariableNode = selectedLibraryKey === 'variables';
  const isRaiseTicketNode = selectedLibraryKey === 'raise_ticket';
  const isChatHandoffNode = selectedLibraryKey === 'chat_handoff';
  const isHandoffNode = isRaiseTicketNode || isChatHandoffNode;
  const supportsResponseMapping = false;
  const supportsRequestSection = !isFunctionNode && !isVariableNode && !isDatabaseNode;
  const normalizedSavedApis = Array.isArray(savedApis) ? savedApis : [];
  const normalizedSavedFunctions = Array.isArray(savedFunctions) ? savedFunctions : [];
  const requestConfig = selectedNode?.data?.config?.asyncRequest || selectedConfig;
  const selectedSavedApi = normalizedSavedApis.find((item) => (
    item.apiId === requestConfig.apiDefinitionId
    || (item.url && requestConfig.url && String(item.url).trim() === String(requestConfig.url).trim())
  )) || null;
  const requestTemplateVariables = getRequestTemplateVariables(requestConfig);
  const panelWidthClass = workspaceSettings
    ? 'w-[min(48vw,720px)]'
    : collapsed
    ? 'w-[64px]'
    : activeTab === 'debugger'
      ? 'w-[420px]'
      : 'w-[390px]';
  const selectedNodeOutgoingEdges = selectedNode
    ? edges.filter((edge) => edge.source === selectedNode.id)
    : [];
  const primaryOutgoingEdge = selectedNodeOutgoingEdges[0] || null;
  const handoffResolvedEdge = isChatHandoffNode
    ? selectedNodeOutgoingEdges.find((edge) => (
        String(edge.sourceHandle || '').trim().toLowerCase() === 'handoff-resolved'
        || String(edge.data?.condition || edge.label || '').trim().toLowerCase() === 'resolved'
      )) || null
    : null;
  const handoffTimeoutEdge = isChatHandoffNode
    ? selectedNodeOutgoingEdges.find((edge) => {
        const handle = String(edge.sourceHandle || '').trim().toLowerCase();
        const condition = String(edge.data?.condition || edge.label || '').trim().toLowerCase();
        return handle === 'handoff-timeout'
          || handle === 'fallback'
          || condition === 'handoff_timeout'
          || condition === 'handoff-timeout'
          || condition === 'fallback'
          || condition.includes('timeout')
          || condition.includes('no agent');
      }) || null
    : null;
  const isApiBranchNode = (selectedNode?.type === 'api_call' && !isFunctionNode) || isDynamicNode;
  const apiFailureEdge = isApiBranchNode
    ? selectedNodeOutgoingEdges.find((edge) => {
        const handle = String(edge.sourceHandle || '').trim().toLowerCase();
        const condition = String(edge.data?.condition || edge.label || '').trim().toLowerCase();
        return handle === 'failure' || ['failure', 'error', 'false'].includes(condition);
      }) || null
    : null;
  const apiSuccessEdge = isApiBranchNode
    ? selectedNodeOutgoingEdges.find((edge) => {
        if (edge === apiFailureEdge) return false;
        const handle = String(edge.sourceHandle || '').trim().toLowerCase();
        const condition = String(edge.data?.condition || edge.label || '').trim().toLowerCase();
        return handle === 'success' || condition === 'success' || (!['failure', 'error', 'false', 'handoff-timeout', 'handoff_timeout', 'fallback', 'exit', 'handoff'].includes(condition) && !['failure', 'exit', 'handoff'].includes(handle));
      }) || null
    : null;
  /** Purpose: Build node IDs and labels for route selectors. Why: Selectors display readable names while storing stable IDs. */
  const nextNodeOptions = nodes
    .filter((node) => node.id !== selectedNode?.id && node.type !== 'start')
    .map((node) => ({ id: node.id, label: node.data?.title || node.id }));
  // const quickReplyBranchRows = isQuickReplyNode
  //   ? normalizedQuickReply.options.map((option, index) => {
  //       const normalizedOption = String(option || '').trim().toLowerCase();
  //       const matchedEdge = selectedNodeOutgoingEdges.find((edge) => (
  //         String(edge.data?.condition || edge.label || '').trim().toLowerCase() === normalizedOption
  //       ));
  //       return { option, sourceHandle: `quick-reply-${index}`, edge: matchedEdge || null };
  //     })
  //   : [];

  const quickReplyBranchRows = isQuickReplyNode
  ? normalizedQuickReply.options.map(
      (option, index) => {
        const normalizedOption =
          normalizeQuickReplyOption(
            option,
            index
          );

        const title = String(
          normalizedOption.title || ''
        ).trim();

        const text = String(
          normalizedOption.text || ''
        ).trim();

        const sourceHandle =
          `quick-reply-${index}`;

        /** Purpose: Provide the matched edge operation for the flow properties panel module. Why: Callers depend on this shared result to keep the surrounding flow consistent. */
        const matchedEdge =
          selectedNodeOutgoingEdges.find(
            (edge) => {
              // Best match = exact handle
              if (
                edge.sourceHandle ===
                sourceHandle
              ) {
                return true;
              }

              const condition = String(
                edge.data?.condition ||
                  edge.label ||
                  ''
              )
                .trim()
                .toLowerCase();

              return (
                condition ===
                  text.toLowerCase() ||
                condition ===
                  title.toLowerCase()
              );
            }
          );

        return {
          option: title,
          value: text,
          sourceHandle,
          edge: matchedEdge || null,
        };
      }
    )
  : [];
  const quickReplyFallbackEdge = isQuickReplyNode
    ? selectedNodeOutgoingEdges.find((edge) => String(edge.data?.condition || edge.label || '').trim().toLowerCase() === 'fallback') || null
    : null;

  // Purpose: upload an image asset and attach the returned URL to a node. Why: media nodes store server-hosted references rather than browser-local files.
  const handleImageUpload = async (nodeId, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append('media', file);
    try {
      const response = await axios.post('/api/v1/flows/media', formData);
      const uploadedMedia = response.data || {};
      onNodeChange(nodeId, {
        data: {
          config: {
            mediaUrl: uploadedMedia.mediaUrl || '',
            mediaType: uploadedMedia.mediaType || (file.type.startsWith('video/') ? 'video' : file.type.startsWith('image/') ? 'image' : 'file'),
            alt: uploadedMedia.fileName || file.name,
          },
        },
      });
    } catch (error) {
      window.alert(error.response?.data?.error || 'Media upload failed. Please try again.');
    }
  };

  return (
    <aside
      className={[
        'flex h-full flex-col border-l border-slate-200 bg-gradient-to-b from-white via-white to-slate-50/95 text-slate-900 shadow-[-18px_0_50px_rgba(15,23,42,0.08)] backdrop-blur-xl transition-all duration-300 dark:border-white/10 dark:from-slate-950 dark:via-slate-950 dark:to-slate-950/95 dark:text-slate-100 dark:shadow-2xl',
        fullscreen
          ? 'fixed inset-3 z-[100] h-auto w-auto rounded-[28px] border shadow-[0_30px_100px_rgba(15,23,42,0.35)] md:inset-6'
          : panelWidthClass,
      ].join(' ')}
    >
      {collapsed && !fullscreen ? (
        <div className="flex flex-1 flex-col items-center gap-3 px-2 py-4 text-[10px] uppercase tracking-[0.24em] text-slate-500 dark:text-slate-500">
          {!isDynamicNode ? <button
            type="button"
            onClick={() => {
              onTabChange?.('inspector');
              onToggle?.();
            }}
            className="rounded-xl px-2 py-2 text-slate-500 transition hover:bg-cyan-50 hover:text-cyan-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-cyan-200"
          >
            Edit
          </button> : null}
          {!isDynamicNode ? <button
            type="button"
            onClick={() => {
              onTabChange?.('debugger');
              onToggle?.();
            }}
            className="rounded-xl px-2 py-2 text-slate-500 transition hover:bg-violet-50 hover:text-violet-700 dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-violet-200"
          >
            Test
          </button> : null}
          {isDynamicNode ? <button type="button" onClick={onToggleFullscreen} className="rounded-xl px-2 py-2 text-cyan-700 transition hover:bg-cyan-50 dark:text-cyan-200 dark:hover:bg-white/10" title="Open Dynamic Chat editor">Open</button> : null}
        </div>
      ) : (
        <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden">
          <div className="border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur dark:border-white/10 dark:bg-slate-950/70">
            <div className="flex items-center gap-2">
              <div className={`${fullscreen && isDynamicNode ? 'flex w-auto shrink-0' : `grid min-w-0 flex-1 ${isDynamicNode ? 'grid-cols-1' : 'grid-cols-3'}`} gap-1 rounded-2xl border border-slate-200 bg-slate-100/80 p-1 shadow-inner dark:border-white/5 dark:bg-white/5`}>
                <button
                  type="button"
                  onClick={() => onTabChange?.('inspector')}
                  className={[
                    `rounded-xl px-3 py-2 text-sm font-semibold transition ${fullscreen && isDynamicNode ? 'whitespace-nowrap' : ''}`,
                    visibleTab === 'inspector'
                      ? 'bg-white text-cyan-700 shadow-sm ring-1 ring-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-100 dark:ring-cyan-400/20'
                      : 'text-slate-500 hover:bg-white/70 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200',
                  ].join(' ')}
                >
                  {fullscreen && isDynamicNode ? 'Dynamic Chat setup' : workspaceSettings ? 'Settings' : 'Edit mode'}
                </button>
                {!isDynamicNode ? <button
                  type="button"
                  onClick={() => onTabChange?.('variables')}
                  className={[
                    'rounded-xl px-3 py-2 text-sm font-semibold transition',
                    activeTab === 'variables'
                      ? 'bg-white text-cyan-700 shadow-sm ring-1 ring-cyan-200 dark:bg-cyan-500/15 dark:text-cyan-100 dark:ring-cyan-400/20'
                      : 'text-slate-500 hover:bg-white/70 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200',
                  ].join(' ')}
                >
                  Variables
                </button> : null}
                {!isDynamicNode ? <button
                  type="button"
                  onClick={() => onTabChange?.('debugger')}
                  className={[
                    'rounded-xl px-3 py-2 text-sm font-semibold transition',
                    activeTab === 'debugger'
                      ? 'bg-white text-violet-700 shadow-sm ring-1 ring-violet-200 dark:bg-violet-500/15 dark:text-violet-100 dark:ring-violet-400/20'
                      : 'text-slate-500 hover:bg-white/70 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-white/5 dark:hover:text-slate-200',
                  ].join(' ')}
                >
                  Bot preview
                </button> : null}
              </div>
              {isDynamicNode && !fullscreen && !workspaceSettings ? <button
                type="button"
                onClick={onToggleFullscreen}
                className="rounded-xl border border-cyan-200 bg-cyan-50 p-2 text-cyan-700 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-200 dark:hover:bg-cyan-500/20"
                aria-label="Open Dynamic Chat in full screen"
                title="Open focused editor"
              >
                <span aria-hidden="true">⛶</span>
              </button> : null}
              {workspaceSettings ? <button type="button" onClick={onToggleFullscreen} aria-label="Close Dynamic Chat settings" title="Close settings" className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-lg text-slate-600 shadow-sm transition hover:border-cyan-300 hover:text-cyan-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">×</button> : null}
              {fullscreen && isDynamicNode ? <>
                <button type="button" onClick={onSaveFlow} disabled={saving || loading || isReadOnly} className="rounded-xl bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50" title={isReadOnly ? 'Switch to DEV to save flow changes' : 'Save all flow changes'}>
                  {saving ? 'Saving…' : 'Save flow'}
                </button>
                <button type="button" onClick={onToggleFullscreen} aria-label="Close Dynamic Chat setup" title="Close (Esc)" className="grid h-9 w-9 place-items-center rounded-xl border border-slate-200 bg-white text-lg text-slate-600 shadow-sm transition hover:border-cyan-300 hover:text-cyan-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">×</button>
              </> : null}
              {!fullscreen && !workspaceSettings ? <button
                type="button"
                onClick={onToggle}
                className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50 hover:text-cyan-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-cyan-400/40 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                aria-label={collapsed ? 'Expand inspector' : 'Collapse inspector'}
              >
                <span className="block h-4 w-4">{collapsed ? '‹' : '›'}</span>
              </button> : null}
            </div>
          </div>

          <div className={`${fullscreen && isDynamicNode ? 'flex min-h-0 flex-1 overflow-hidden' : 'flex-1 overflow-y-auto'} [scrollbar-color:rgba(100,116,139,0.35)_transparent]`}>
            {fullscreen && isDynamicNode ? <nav aria-label="Dynamic Chat sections" className="hidden w-56 shrink-0 overflow-y-auto border-r border-slate-200 bg-slate-50/80 p-4 dark:border-white/10 dark:bg-slate-950/50 md:block">
              <p className="mb-3 px-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Setup sections</p>
              <div className="space-y-1">
                {[
                  ['dynamic-chat-routes', 'Conversation routes'],
                  ['dynamic-chat-goal', 'Goal & instructions'],
                  ['dynamic-chat-response', 'Saved reply'],
                  ['dynamic-chat-mode', 'Conversation mode'],
                  ['dynamic-chat-context', 'Context & history'],
                  ...(selectedNode?.data?.config?.conversationMode ? [
                    ['dynamic-chat-collect', 'Information to collect'],
                    ['dynamic-chat-actions', 'Optional actions'],
                    ['dynamic-chat-messages', 'Messages & limits'],
                  ] : []),
                  ['dynamic-chat-advanced', 'Advanced AI settings'],
                ].map(([sectionId, label]) => <button key={sectionId} type="button" onClick={() => { const target = document.getElementById(sectionId); if (target?.tagName === 'DETAILS') target.open = true; target?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }} className="w-full rounded-xl px-3 py-2.5 text-left text-xs font-medium text-slate-600 transition hover:bg-white hover:text-cyan-700 hover:shadow-sm dark:text-slate-300 dark:hover:bg-white/5 dark:hover:text-cyan-200">{label}</button>)}
              </div>
            </nav> : null}
            <div className={`${fullscreen && isDynamicNode ? 'min-w-0 flex-1 overflow-y-auto p-5 md:p-7' : 'p-4'}`}>
            {visibleTab === 'debugger' ? (
              <FlowDebuggerPanel {...debuggerProps} />
            ) : visibleTab === 'variables' ? (
              <VariableManager
                key={`${currentBotId}:${currentEnvironment}`}
                variables={variables}
                nodes={nodes}
                edges={edges}
                onAddVariable={onAddVariable}
                onUpdateVariable={onUpdateVariable}
                onDeleteVariable={onDeleteVariable}
                onSaveGlobals={saveGlobalVariables}
                canManageGlobals={registry.canManageGlobals}
                registryLoading={registryLoading}
                registryError={registryError}
                onRetry={() => setRegistryRetry((count) => count + 1)}
              />
            ) : (
              <div className="flex flex-col gap-4">
                {!selectedNode && !selectedEdge ? (
                  <>
                    <div className={GUIDE_CARD_CLASS}>
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-700 dark:text-cyan-200">How to build</div>
                      <div className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                        <div>1. Click `Add step` or use the `+` button on a node.</div>
                        <div>2. Select a node, then configure its prompt, options, and variable here.</div>
                        <div>3. Connect the right-side handle to the next node.</div>
                        <div>4. Click `Save flow` to keep your changes.</div>
                      </div>
                    </div>

                    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-white/5">
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">How to test</div>
                      <div className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                        <div>1. Open the `Bot preview` tab.</div>
                        <div>2. Use `Home` to start the default journey.</div>
                        <div>3. Send a message and review the logs below to see what happened next.</div>
                      </div>
                    </div>
                  </>
                ) : null}

                {selectedNode ? (
                  <div className={PANEL_CARD_CLASS}>
                    <div className="-mx-5 -mt-5 mb-5 rounded-t-[27px] border-b border-slate-200 bg-gradient-to-br from-cyan-50 via-white to-violet-50/70 px-5 py-4 dark:border-white/10 dark:from-cyan-500/10 dark:via-slate-900/30 dark:to-violet-500/10">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-white/80 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-cyan-700 shadow-sm dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-200">
                            <span className="h-1.5 w-1.5 rounded-full bg-cyan-500" />
                            Selected node
                          </div>
                          <EditableNodeTitle
                            key={selectedNode.id}
                            title={selectedNode.data?.title || ''}
                            readOnly={currentEnvironment === 'prod'}
                            onChange={(title) => onNodeChange(selectedNode.id, { data: { title } })}
                          />
                          <div className="mt-1 truncate font-mono text-[11px] text-slate-400 dark:text-slate-500">
                          {selectedNode.id}
                          </div>
                          {selectedNode.type === 'start' ? (
                            <div className="mt-2 inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:border-emerald-400/20 dark:bg-emerald-500/10 dark:text-emerald-200">
                              Default start node
                            </div>
                          ) : null}
                        </div>
                        <button
                          type="button"
                          onClick={onClearSelection}
                          className="rounded-xl border border-slate-200 bg-white/80 px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm transition hover:border-cyan-300 hover:text-cyan-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {selectedNode.type === 'message' && (isMediaNode || isJumpNode || isSetLanguageNode || isDetectLanguageNode || isChatHandoffNode) ? (
                        <Field label="Localization key (optional)">
                          <LocalizationKeyPicker
                            label="Node localization key"
                            normalTextLabel="Manual content (no localization key)"
                            localizationKey={selectedNode.data?.config?.localizationKey || ''}
                            localizationKeys={localizationKeys}
                            selectClass={selectClass}
                            onKeyChange={(localizationKey) => onNodeChange(selectedNode.id, { data: { config: { localizationKey } } })}
                          />
                          {!localizationKeys.length ? <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">No keys found. Create keys from Orchestration → Localization.</div> : null}
                        </Field>
                      ) : null}

                      {selectedNode.type !== 'end' && primaryOutgoingEdge && !isChatHandoffNode && !isApiBranchNode ? (
                        <Field label="Next node">
                          <select
                            value={primaryOutgoingEdge.target || ''}
                            onChange={(event) => onEdgeChange?.(primaryOutgoingEdge.id, {
                              target: event.target.value,
                            })}
                            className={selectClass}
                          >
                            {nextNodeOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                      ) : null}

                      {isApiBranchNode && !isDatabaseNode ? (
                        <details id={isDynamicNode ? 'dynamic-chat-routes' : undefined} open={!isDynamicNode} className="scroll-mt-6 overflow-hidden rounded-2xl border border-cyan-200 bg-cyan-50/70 dark:border-cyan-500/20 dark:bg-cyan-950/20">
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 marker:hidden [&::-webkit-details-marker]:hidden">
                            <span><span className="block text-sm font-semibold text-cyan-800 dark:text-cyan-200">{isDynamicNode ? 'Conversation routes' : 'API routing'}</span><span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">Choose where each outcome continues</span></span>
                            <span className="rounded-full bg-white/80 px-2 py-1 text-[10px] font-semibold text-cyan-800 dark:bg-white/10 dark:text-cyan-200">{isDynamicNode && selectedNode.data?.config?.conversationMode ? '4 outcomes' : '2 outcomes'}</span>
                          </summary>
                          <div className="space-y-3 border-t border-cyan-200/70 p-4 dark:border-cyan-500/20">

                          {[
                            {
                              key: 'success',
                              label: isDynamicNode ? (selectedNode.data?.config?.conversationMode ? 'Completed → next node' : 'AI Generated (Success) next node') : 'Success (2xx) next node',
                              edge: apiSuccessEdge,
                              sourceHandle: 'success',
                              condition: 'success',
                              edgeLabel: 'Success',
                            },
                            {
                              key: 'failure',
                              label: isDynamicNode ? 'Provider Failure next node' : 'Failure (4xx / 5xx / timeout) next node',
                              edge: apiFailureEdge,
                              sourceHandle: 'failure',
                              condition: 'failure',
                              edgeLabel: 'Failure',
                            },
                            ...(isDynamicNode && selectedNode.data?.config?.conversationMode ? ['exit', 'handoff'].map((branch) => ({
                              key: branch, label: `${branch === 'exit' ? 'User exit' : 'Human assistance'} → next node`,
                              edge: selectedNodeOutgoingEdges.find((edge) => edge.sourceHandle === branch || edge.data?.condition === branch),
                              sourceHandle: branch, condition: branch, edgeLabel: branch,
                            })) : []),
                          ].map((route) => (
                            <Field key={route.key} label={route.label}>
                              <select
                                value={route.edge?.target || ''}
                                onChange={(event) => {
                                  const target = event.target.value;
                                  if (!target) {
                                    if (route.edge) onDeleteEdge?.(route.edge.id);
                                    return;
                                  }
                                  if (route.edge) {
                                    onEdgeChange?.(route.edge.id, {
                                      target,
                                      sourceHandle: route.sourceHandle,
                                      label: route.edgeLabel,
                                      data: {
                                        ...(route.edge.data || {}),
                                        condition: route.condition,
                                      },
                                    });
                                    return;
                                  }
                                  onCreateEdge?.(selectedNode.id, target, {
                                    sourceHandle: route.sourceHandle,
                                    label: route.edgeLabel,
                                    data: { condition: route.condition },
                                  });
                                }}
                                className={selectClass}
                              >
                                <option value="">Select target node</option>
                                {nextNodeOptions.map((option) => (
                                  <option key={`${route.key}-${option.id}`} value={option.id}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          ))}
                          </div>
                        </details>
                      ) : null}

                      {isChatHandoffNode ? (
                        <div className="space-y-3 rounded-3xl border border-sky-200 bg-sky-50/70 p-4 dark:border-sky-400/20 dark:bg-sky-500/10">
                          <div>
                            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700 dark:text-sky-200">
                              Handoff routing
                            </div>
                          </div>

                          <Field label="Store handoff result in variable (optional)">
                            <VariableField
                              value={selectedNode.data?.config?.handoffResultVariable || ''}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: { config: { handoffResultVariable: value } },
                              })}
                              variables={variables}
                              placeholder="journey.handoff_result"
                            />
                            <p className="mt-2 text-xs text-slate-500">
                              Stores an object with ticketId (8 digits), agent.id, agent.name, status,
                              statusCode and error.message. Updated on resolution or queue timeout.
                              Leave blank to skip result storage. Ticket reference is also available as ticket_number.
                            </p>
                          </Field>

                          {[
                            {
                              key: 'resolved',
                              label: 'Resolved next node',
                              edge: handoffResolvedEdge,
                              sourceHandle: 'handoff-resolved',
                              condition: 'resolved',
                              edgeLabel: 'Resolved',
                            },
                            {
                              key: 'timeout',
                              label: 'Timeout / No agent next node',
                              edge: handoffTimeoutEdge,
                              sourceHandle: 'handoff-timeout',
                              condition: 'handoff_timeout',
                              edgeLabel: 'Timeout / No agent',
                            },
                          ].map((route) => (
                            <Field key={route.key} label={route.label}>
                              <select
                                value={route.edge?.target || ''}
                                onChange={(event) => {
                                  const target = event.target.value;
                                  if (!target) {
                                    if (route.edge) onDeleteEdge?.(route.edge.id);
                                    return;
                                  }
                                  if (route.edge) {
                                    onEdgeChange?.(route.edge.id, {
                                      target,
                                      sourceHandle: route.sourceHandle,
                                      label: route.edgeLabel,
                                      data: {
                                        ...(route.edge.data || {}),
                                        condition: route.condition,
                                      },
                                    });
                                    return;
                                  }
                                  onCreateEdge?.(selectedNode.id, target, {
                                    sourceHandle: route.sourceHandle,
                                    label: route.edgeLabel,
                                    data: { condition: route.condition },
                                  });
                                }}
                                className={selectClass}
                              >
                                <option value="">Select target node</option>
                                {nextNodeOptions.map((option) => (
                                  <option key={`${route.key}-${option.id}`} value={option.id}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </Field>
                          ))}
                        </div>
                      ) : null}

                      {selectedNode.type === 'message' && (
                        <div className="space-y-4">
                          {isSetLanguageNode ? (
                            <div className="space-y-4 rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 dark:border-emerald-400/20 dark:bg-emerald-500/10">
                              <Field label="Conversation language">
                                <select
                                  value={selectedNode.data?.config?.languageCode || 'hi'}
                                  onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { languageCode: event.target.value } } })}
                                  className={selectClass}
                                >
                                  {CONVERSATION_LANGUAGES.map((language) => (
                                    <option key={language.code} value={language.code}>{language.label} ({language.code})</option>
                                  ))}
                                </select>
                              </Field>
                            </div>
                          ) : null}
                          {isDetectLanguageNode ? (
                            <div className="space-y-4 rounded-2xl border border-teal-200 bg-teal-50/70 p-4 dark:border-teal-400/20 dark:bg-teal-500/10">
                              <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-teal-800 dark:text-teal-300">
                                <TranslateOutlinedIcon sx={{ fontSize: 16 }} />
                                <span>Bharat Smart Language Detection</span>
                              </div>
                              <p className="text-xs text-teal-900/80 dark:text-teal-200/80">
                                Automatically recognizes Indian scripts (Devanagari, Gujarati, Bengali, Tamil, Telugu, Kannada, Malayalam, Punjabi) and Hinglish slang without requiring an explicit language menu.
                              </p>
                              <Field label="Save detected code to variable">
                                <input
                                  type="text"
                                  value={selectedNode.data?.config?.saveToVariable || 'detected_language'}
                                  onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { saveToVariable: event.target.value } } })}
                                  placeholder="detected_language"
                                  className={inputClass}
                                />
                              </Field>
                              <Field label="Fallback language">
                                <select
                                  value={selectedNode.data?.config?.fallbackLanguage || 'en'}
                                  onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { fallbackLanguage: event.target.value } } })}
                                  className={selectClass}
                                >
                                  {CONVERSATION_LANGUAGES.map((language) => (
                                    <option key={language.code} value={language.code}>{language.label} ({language.code})</option>
                                  ))}
                                </select>
                              </Field>
                              <label className="flex items-center gap-2 text-xs font-medium text-slate-700 dark:text-slate-300">
                                <input
                                  type="checkbox"
                                  checked={selectedNode.data?.config?.updateSessionLanguage !== false}
                                  onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { updateSessionLanguage: event.target.checked } } })}
                                  className="rounded text-teal-600 focus:ring-teal-500"
                                />
                                <span>Update session language automatically (adapts next messages)</span>
                              </label>
                            </div>
                          ) : null}
                          {isMediaNode ? (
                            <>
                              <Field label="Media preview">
                                <div className="overflow-hidden rounded-3xl border border-slate-200 bg-slate-50 dark:border-white/10 dark:bg-slate-950/80">
                                  {selectedNode.data?.config?.mediaUrl ? (
                                    selectedNode.data?.config?.mediaType === 'video' ? (
                                      <video
                                        src={selectedNode.data?.config?.mediaUrl}
                                        controls
                                        className="h-44 w-full object-cover"
                                      />
                                    ) : (
                                    <img
                                      src={selectedNode.data?.config?.mediaUrl}
                                      alt={selectedNode.data?.config?.alt || selectedNode.data?.title || 'Node image'}
                                      className="h-44 w-full object-cover"
                                    />
                                    )
                                  ) : (
                                    <div className="flex h-44 items-center justify-center text-sm text-slate-500">
                                      No media selected yet
                                    </div>
                                  )}
                                </div>
                              </Field>

                              <Field label="Media URL">
                                <input
                                  value={selectedNode.data?.config?.mediaUrl || ''}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        mediaUrl: event.target.value,
                                        mediaType: selectedNode.data?.config?.mediaType || (selectedLibraryKey === 'video' ? 'video' : selectedLibraryKey === 'file' ? 'file' : 'image'),
                                      },
                                    },
                                  })}
                                  className={inputClass}
                                  placeholder="https://example.com/asset"
                                />
                              </Field>

                              <Field label="Upload media">
                                <input
                                  type="file"
                                  accept={selectedLibraryKey === 'video' ? 'video/*' : selectedLibraryKey === 'file' ? '*' : 'image/*'}
                                  onChange={(event) => handleImageUpload(selectedNode.id, event.target.files?.[0])}
                                  className={`${inputClass} file:mr-3 file:rounded-xl file:border-0 file:bg-cyan-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-cyan-700 dark:file:bg-cyan-500/15 dark:file:text-cyan-100`}
                                />
                              </Field>

                              <Field label="Alt text">
                                <input
                                  value={selectedNode.data?.config?.alt || ''}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: { alt: event.target.value },
                                    },
                                  })}
                                  className={inputClass}
                                  placeholder="Describe the image"
                                />
                              </Field>

                              <Field label="Caption">
                                <TemplateTextarea
                                  value={selectedNode.data?.config?.message || ''}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: { message: value, prompt: value },
                                    },
                                  })}
                                  variables={variables}
                                  className={`${inputClass} min-h-[120px] resize-none`}
                                  placeholder="Write image caption here"
                                />
                              </Field>
                            </>
                          ) : (
                            <div className="space-y-4">
                              {!isJumpNode && <Field id={isDynamicNode ? 'dynamic-chat-goal' : undefined} label={isDynamicNode ? (selectedNode.data?.config?.conversationMode ? 'Conversation goal & instructions' : 'AI prompt') : isQuickReplyNode ? 'Prompt' : 'Message'} helpText={isDynamicNode ? 'Describe what the AI should help with. Add information to collect in the section below.' : undefined}>
                                <LocalizedContent key={selectedNode.id} localizationKey={selectedConfig.localizationKey} localizationKeys={localizationKeys} selectClass={selectClass} onKeyChange={(localizationKey) => onNodeChange(selectedNode.id, { data: { config: { localizationKey } } })}>
                                <TemplateTextarea
                                  value={
                                    isDynamicNode || isQuickReplyNode
                                      ? (selectedNode.data?.config?.prompt ?? selectedNode.data?.config?.message ?? (isQuickReplyNode ? normalizedQuickReply.prompt : ''))
                                      : (selectedNode.data?.config?.message ?? selectedNode.data?.config?.prompt ?? '')
                                  }
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        message: value,
                                        prompt: value,
                                      },
                                    },
                                  })}
                                  variables={variables}
                                  title={isDynamicNode ? 'Dynamic Chat Prompt & Instructions' : isQuickReplyNode ? 'Quick Reply Prompt' : 'Message Editor'}
                                  subtitle={isDynamicNode ? 'Full-screen prompt editor with variable autocomplete and starter templates' : 'Compose full-screen message with variables'}
                                  allowFullscreen={isDynamicNode}
                                  className={`${inputClass} min-h-[140px] resize-y`}
                                  placeholder={isDynamicNode ? "Write conversation guidelines... e.g. Please ask customer for name and store in {variables.name}, then ask for mobile number in {variables.mobile}" : "Hi there, how can I help?"}
                                />
                                </LocalizedContent>
                              </Field>}

                              {!isDynamicNode && !isJumpNode ? <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
                                <Field label="Translation language">
                                  <select value={translationLanguage} onChange={(event) => setTranslationLanguage(event.target.value)} className={selectClass}>
                                    {CONVERSATION_LANGUAGES.filter((language) => language.code !== 'en').map((language) => <option key={language.code} value={language.code}>{language.label} ({language.code})</option>)}
                                  </select>
                                </Field>
                                <Field label={`${translationLanguageLabel} ${isQuickReplyNode ? 'prompt' : 'message'}`}>
                                <TemplateTextarea
                                  value={selectedNode.data?.config?.translations?.[translationLanguage]?.[isQuickReplyNode ? 'prompt' : 'message'] ?? selectedNode.data?.config?.translations?.[translationLanguage]?.message ?? selectedNode.data?.config?.translations?.[translationLanguage]?.prompt ?? ''}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        translations: {
                                          ...(selectedNode.data?.config?.translations || {}),
                                          [translationLanguage]: {
                                            ...(selectedNode.data?.config?.translations?.[translationLanguage] || {}),
                                            [isQuickReplyNode ? 'prompt' : 'message']: value,
                                            message: value,
                                            prompt: value,
                                          },
                                        },
                                      },
                                    },
                                  })}
                                  variables={variables}
                                  className={`${inputClass} min-h-[110px] resize-none`}
                                  placeholder={`Write the ${translationLanguageLabel} translation`}
                                />
                                </Field>
                              </div> : null}

                              {isQuickReplyNode ? (
                                <>
                                  <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/70 p-2 dark:border-slate-800 dark:bg-slate-900/60">
                                    <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">Option Source</span>
                                    <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-medium dark:border-slate-700 dark:bg-slate-800">
                                      <button
                                        type="button"
                                        onClick={() => onNodeChange(selectedNode.id, {
                                          data: { config: { optionsMode: 'static' } },
                                        })}
                                        className={`rounded-md px-2.5 py-1 transition ${
                                          (selectedNode.data?.config?.optionsMode || 'static') === 'static'
                                            ? 'bg-sky-500 font-semibold text-white shadow-xs'
                                            : 'text-slate-600 hover:text-slate-900 dark:text-slate-300'
                                        }`}
                                      >
                                        Static
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => onNodeChange(selectedNode.id, {
                                          data: { config: { optionsMode: 'dynamic' } },
                                        })}
                                        className={`rounded-md px-2.5 py-1 transition ${
                                          selectedNode.data?.config?.optionsMode === 'dynamic'
                                            ? 'bg-sky-500 font-semibold text-white shadow-xs'
                                            : 'text-slate-600 hover:text-slate-900 dark:text-slate-300'
                                        }`}
                                      >
                                        Dynamic (Variable)
                                      </button>
                                    </div>
                                  </div>

                                  {selectedNode.data?.config?.optionsMode === 'dynamic' ? (
                                    <div className="mb-3 rounded-xl border border-sky-200/80 bg-sky-50/50 p-3 dark:border-sky-500/20 dark:bg-sky-950/20">
                                      <div className="mb-1 text-[11px] font-semibold text-sky-800 dark:text-sky-300">
                                        Dynamic Options Variable
                                      </div>
                                      <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
                                        Map an array or object from a Function, API response, or Journey variable (e.g. <code>api_result.items</code> or <code>journey.menuOptions</code>).
                                      </p>
                                      <VariableField
                                        value={selectedNode.data?.config?.dynamicOptionsVariable || ''}
                                        onChange={(value) => onNodeChange(selectedNode.id, {
                                          data: { config: { dynamicOptionsVariable: value } },
                                        })}
                                        variables={variables}
                                        placeholder="journey.menuOptions or api_result.items"
                                      />
                                    </div>
                                  ) : (
                                    <OptionsEditor
                                      value={normalizedQuickReply.options}
                                      onChange={(value) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { options: value },
                                        },
                                      })}
                                    />
                                  )}
                                  <Field label="Store answer in variable">
                                    <VariableField
                                      value={selectedNode.data?.config?.variable || selectedNode.data?.config?.variableName || ''}
                                      onChange={(value) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { variable: value, variableName: value },
                                        },
                                      })}
                                      variables={variables}
                                      placeholder="user_reply"
                                    />
                                  </Field>
                                  <QuickReplyRoutingEditor
                                    rows={quickReplyBranchRows}
                                    fallbackEdge={quickReplyFallbackEdge}
                                    nextNodeOptions={nextNodeOptions}
                                    onEdgeChange={onEdgeChange}
                                    onCreateEdge={onCreateEdge}
                                    onDeleteEdge={onDeleteEdge}
                                    sourceNodeId={selectedNode.id}
                                  />
                                </>
                              ) : null}

                              {isListNode ? (
                                <>
                                  <Field label="List title">
                                    <input
                                      value={selectedNode.data?.config?.title || selectedNode.data?.config?.prompt || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { title: event.target.value, prompt: event.target.value },
                                        },
                                      })}
                                      className={inputClass}
                                      placeholder="Choose an option"
                                    />
                                  </Field>
                                  <Field label="Store selection in variable">
                                    <VariableField
                                      value={selectedNode.data?.config?.variable || selectedNode.data?.config?.variableName || ''}
                                      onChange={(value) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { variable: value, variableName: value },
                                        },
                                      })}
                                      variables={variables}
                                      placeholder="list_selection"
                                    />
                                  </Field>
                                  <ListItemsEditor
                                    items={selectedNode.data?.config?.listItems || []}
                                    onChange={(value) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: { listItems: value },
                                      },
                                    })}
                                  />
                                </>
                              ) : null}

                              {isDynamicNode ? (
                                <>
                                  <section id="dynamic-chat-response" className="scroll-mt-6 rounded-2xl border border-cyan-100 bg-cyan-50/50 p-3 dark:border-cyan-400/20 dark:bg-cyan-950/20">
                                    <Field label={selectedNode.data?.config?.conversationMode ? 'Save latest AI reply to' : 'Save AI reply to'} helpText={selectedNode.data?.config?.conversationMode ? 'Stores the most recent assistant reply—not the full conversation. Leave blank if later nodes do not need it.' : 'Optional. Save the generated reply for use in later nodes.'}>
                                      <VariableField value={selectedNode.data?.config?.responseVariable || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { responseVariable: value } } })} variables={variables} placeholder="journey.ai_reply" />
                                    </Field>
                                  </section>
                                  <DynamicChatSettings config={selectedNode.data?.config || {}} onChange={(config) => onNodeChange(selectedNode.id, { data: { config } })} availableFlows={availableFlows} nodes={nodes} currentNodeId={selectedNode.id} currentFlowId={currentFlowId} variables={variables} />
                                  <details id="dynamic-chat-advanced" className="scroll-mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-white/10 dark:bg-slate-900/70">
                                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3.5 marker:hidden [&::-webkit-details-marker]:hidden"><span><span className="block text-sm font-semibold text-slate-800 dark:text-slate-100">Advanced AI settings</span><span className="mt-0.5 block text-xs font-normal text-slate-500">Behavior and generation limits</span></span><span aria-hidden="true" className="text-slate-400">⌄</span></summary>
                                    <div className="space-y-4 border-t border-slate-100 p-4 dark:border-white/10">
                                      <Field label="AI behavior & guardrails" helpText="Set tone, persona and safety rules. The conversation goal above takes priority for the task.">
                                        <TemplateTextarea
                                          value={selectedNode.data?.config?.systemPrompt || ''}
                                          onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { systemPrompt: value } } })}
                                          variables={variables}
                                          title="AI behavior & guardrails"
                                          subtitle="Set persona, tone, and safety instructions"
                                          allowFullscreen={isDynamicNode}
                                          className={`${inputClass} min-h-[120px] resize-y`}
                                          placeholder="Be concise, friendly, and never invent policy details."
                                        />
                                      </Field>
                                      <Field label="Fallback reply" helpText="Shown to the user if the AI provider request fails.">
                                        <TemplateTextarea value={selectedNode.data?.config?.failureMessage || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { failureMessage: value } } })} variables={variables} className={`${inputClass} min-h-[86px] resize-none`} placeholder="I could not generate a response right now." />
                                      </Field>
                                      <div className="grid grid-cols-2 gap-3">
                                        <Field label="Creativity (temperature)"><input type="number" min="0" max="2" step="0.1" value={selectedNode.data?.config?.temperature ?? 0.4} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { temperature: Number(event.target.value) } } })} className={inputClass} /></Field>
                                        <Field label="Reply limit (tokens)"><input type="number" min="1" max="4096" value={selectedNode.data?.config?.maxTokens ?? 300} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { maxTokens: Number(event.target.value) } } })} className={inputClass} /></Field>
                                      </div>
                                    </div>
                                  </details>
                                </>
                              ) : null}

                              {isJumpNode ? (
                                <>
                                  <Field label="Target flow">
                                    <select
                                      value={selectedNode.data?.config?.targetFlowId || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { targetFlowId: event.target.value },
                                        },
                                      })}
                                      className={selectClass}
                                    >
                                      <option value="">Select a flow</option>
                                      {selectedNode.data?.config?.targetFlowId && !availableFlows.some((flow) => flow.flowId === selectedNode.data?.config?.targetFlowId) && (
                                        <option value={selectedNode.data.config.targetFlowId} disabled>
                                          ⚠️ Missing / Deleted Flow ({String(selectedNode.data.config.targetFlowId).slice(0, 8)}...)
                                        </option>
                                      )}
                                      {availableFlows.filter((flow) => flow.flowId !== currentFlowId).map((flow) => (
                                        <option key={flow.flowId} value={flow.flowId}>{flow.name}</option>
                                      ))}
                                    </select>
                                  </Field>
                                  {selectedNode.data?.config?.targetFlowId && !availableFlows.some((flow) => flow.flowId === selectedNode.data?.config?.targetFlowId) && (
                                    <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-2.5 text-xs text-rose-400 space-y-2">
                                      <div>⚠️ The referenced journey ({selectedNode.data.config.targetFlowId}) does not exist or was deleted. Please select an active flow from the dropdown above, or unlink below:</div>
                                      <button
                                        type="button"
                                        onClick={() => onNodeChange(selectedNode.id, { data: { config: { targetFlowId: '' } } })}
                                        className="inline-flex items-center px-2.5 py-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 text-xs font-semibold border border-rose-500/40 transition"
                                      >
                                        Unlink broken journey
                                      </button>
                                    </div>
                                  )}
                                  <Field label="Pass payload (optional)">
                                    <textarea
                                      key={`${selectedNode.id}-switch-payload`}
                                      defaultValue={JSON.stringify(selectedNode.data?.config?.switchPayload || {}, null, 2)}
                                      onBlur={(event) => {
                                        try {
                                          const payload = event.target.value.trim() ? JSON.parse(event.target.value) : {};
                                          if (!payload || Array.isArray(payload) || typeof payload !== 'object') return;
                                          onNodeChange(selectedNode.id, { data: { config: { switchPayload: payload } } });
                                        } catch (_) {}
                                      }}
                                      className={`${inputClass} min-h-[110px] resize-y font-mono text-xs`}
                                      placeholder='{"customerId":"{{customer_id}}"}'
                                    />
                                  </Field>
                                  <Checkbox
                                    label="Return after jump"
                                    checked={Boolean(selectedNode.data?.config?.returnAfterJump)}
                                    onChange={(value) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: { returnAfterJump: value },
                                      },
                                    })}
                                  />
                                </>
                              ) : null}

                              {isHandoffNode ? (
                                <>
                                  <Field label="Live Agent Provider">
                                    <select value={selectedNode.data?.config?.provider || 'native'} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { provider: event.target.value } } })} className={selectClass}>
                                      <option value="native">Native Inbox</option>
                                      <option value="genesys">Genesys Cloud</option>
                                    </select>
                                  </Field>
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Name *">
                                      <TemplateTextarea value={selectedNode.data?.config?.name || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { name: value } } })} variables={variables} className={`${inputClass} min-h-[72px] resize-none`} placeholder="{{user.name}}" />
                                    </Field>
                                    <Field label="Query *">
                                      <TemplateTextarea value={selectedNode.data?.config?.query || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { query: value } } })} variables={variables} className={`${inputClass} min-h-[72px] resize-none`} placeholder="{{variables.userQuery}}" />
                                    </Field>
                                  </div>
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Mobile (optional)"><TemplateTextarea value={selectedNode.data?.config?.mobile || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { mobile: value } } })} variables={variables} className={`${inputClass} min-h-[64px] resize-none`} placeholder="{{user.mobile}}" /></Field>
                                    <Field label="Email (optional)"><TemplateTextarea value={selectedNode.data?.config?.email || ''} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { email: value } } })} variables={variables} className={`${inputClass} min-h-[64px] resize-none`} placeholder="{{user.email}}" /></Field>
                                  </div>
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Queue ID"><input value={selectedNode.data?.config?.queueId || ''} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { queueId: event.target.value } } })} className={inputClass} placeholder="Optional queue ID" /></Field>
                                    <Field label="Language"><input value={selectedNode.data?.config?.language || ''} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { language: event.target.value } } })} className={inputClass} placeholder="hindi" /></Field>
                                  </div>
                                  <Field label="Required skills">
                                    <CsvTagField value={selectedNode.data?.config?.skills || []} onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { skills: value } } })} />
                                  </Field>
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field
                                      label="Assignment mode"
                                    >
                                      <select
                                        value={selectedNode.data?.config?.assignmentMode || 'auto'}
                                        onChange={(event) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { assignmentMode: event.target.value },
                                          },
                                        })}
                                        className={selectClass}
                                      >
                                        <option value="auto">Auto assign</option>
                                        <option value="queue_only">Queue only</option>
                                        <option value="manual">Manual assignment</option>
                                      </select>
                                    </Field>
                                    <Field label="Priority">
                                      <select
                                        value={selectedNode.data?.config?.priority || 'medium'}
                                        onChange={(event) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { priority: event.target.value },
                                          },
                                        })}
                                        className={selectClass}
                                      >
                                        <option value="low">Low</option>
                                        <option value="normal">Normal</option>
                                        <option value="high">High</option>
                                        <option value="urgent">Urgent</option>
                                      </select>
                                    </Field>
                                  </div>
                                  <Field label="No-agent message">
                                    <TemplateTextarea
                                      value={selectedNode.data?.config?.noAgentMessage || ''}
                                      onChange={(value) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: { noAgentMessage: value },
                                        },
                                      })}
                                      variables={variables}
                                      className={`${inputClass} min-h-[100px] resize-none`}
                                      placeholder="All agents are busy right now. Your request has been queued."
                                    />
                                  </Field>
                                  <Field label="Assigned message">
                                    <TemplateTextarea
                                      value={selectedNode.data?.config?.assignedMessage || ''}
                                      onChange={(value) => onNodeChange(selectedNode.id, {
                                        data: { config: { assignedMessage: value } },
                                      })}
                                      variables={variables}
                                      className={`${inputClass} min-h-[90px] resize-none`}
                                      placeholder="Your chat has been assigned to {{assigned_agent_name}}."
                                    />
                                  </Field>
                                  {isChatHandoffNode ? (
                                    <div className="space-y-3 rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4">
                                      <Checkbox
                                        label="Resume journey when ticket is resolved"
                                        checked={selectedNode.data?.config?.resumeOnResolve !== false}
                                        onChange={(value) => onNodeChange(selectedNode.id, {
                                          data: { config: { resumeOnResolve: value } },
                                        })}
                                      />
                                      <Field label="Queue wait override (seconds)">
                                        <input
                                          type="number"
                                          min="0"
                                          value={selectedNode.data?.config?.queueWaitSeconds ?? ''}
                                          onChange={(event) => onNodeChange(selectedNode.id, {
                                            data: { config: { queueWaitSeconds: event.target.value === '' ? '' : Math.max(0, Number(event.target.value)) } },
                                          })}
                                          className={inputClass}
                                          placeholder="Use queue setting"
                                        />
                                      </Field>
                                      <Field label="Resolved fallback message">
                                        <TemplateTextarea
                                          value={selectedNode.data?.config?.resolvedMessage || ''}
                                          onChange={(value) => onNodeChange(selectedNode.id, {
                                            data: { config: { resolvedMessage: value } },
                                          })}
                                          variables={variables}
                                          className={`${inputClass} min-h-[88px] resize-none`}
                                          placeholder="Your support conversation has been resolved."
                                        />
                                      </Field>
                                    </div>
                                  ) : null}
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Queue name">
                                      <input
                                        value={selectedNode.data?.config?.queueName || ''}
                                        onChange={(event) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { queueName: event.target.value },
                                          },
                                        })}
                                        className={inputClass}
                                        placeholder="priority_support"
                                      />
                                    </Field>
                                    <Field label="Department">
                                      <input
                                        value={selectedNode.data?.config?.department || ''}
                                        onChange={(event) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { department: event.target.value },
                                          },
                                        })}
                                        className={inputClass}
                                        placeholder="billing"
                                      />
                                    </Field>
                                  </div>
                                  <div className="grid gap-4 md:grid-cols-2">
                                    <Field label="Group / team">
                                      <input
                                        value={selectedNode.data?.config?.groupName || ''}
                                        onChange={(event) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { groupName: event.target.value },
                                          },
                                        })}
                                        className={inputClass}
                                        placeholder="tier_1_support"
                                      />
                                    </Field>
                                    <Field label="Routing tags">
                                      <CsvTagField
                                        value={selectedNode.data?.config?.routingTags || []}
                                        onChange={(value) => onNodeChange(selectedNode.id, {
                                          data: {
                                            config: { routingTags: value },
                                          },
                                        })}
                                      />
                                    </Field>
                                  </div>
                                </>
                              ) : null}
                            </div>
                          )}
                        </div>
                      )}

                      {selectedNode.type === 'end' && (
                        <div className="space-y-4">
                          <Field label="End message">
                            <LocalizedContent key={selectedNode.id} localizationKey={selectedConfig.localizationKey} localizationKeys={localizationKeys} selectClass={selectClass} onKeyChange={(localizationKey) => onNodeChange(selectedNode.id, { data: { config: { localizationKey } } })}>
                            <TemplateTextarea
                              value={selectedNode.data?.config?.message || ''}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: { message: value },
                                },
                              })}
                              variables={variables}
                              className={`${inputClass} min-h-[120px] resize-none`}
                              placeholder="Thanks for chatting. Goodbye!"
                            />
                            </LocalizedContent>
                          </Field>
                          <Field label="Translation language">
                            <select value={translationLanguage} onChange={(event) => setTranslationLanguage(event.target.value)} className={selectClass}>
                              {CONVERSATION_LANGUAGES.filter((language) => language.code !== 'en').map((language) => <option key={language.code} value={language.code}>{language.label} ({language.code})</option>)}
                            </select>
                          </Field>
                          <Field label={`${translationLanguageLabel} end message`}>
                            <TemplateTextarea
                              value={selectedNode.data?.config?.translations?.[translationLanguage]?.message || ''}
                              onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { translations: { ...(selectedNode.data?.config?.translations || {}), [translationLanguage]: { ...(selectedNode.data?.config?.translations?.[translationLanguage] || {}), message: value } } } } })}
                              variables={variables}
                              className={`${inputClass} min-h-[100px] resize-none`}
                              placeholder={`Write the ${translationLanguageLabel} ending`}
                            />
                          </Field>
                        </div>
                      )}

                      {selectedNode.type === 'input' && (
                        <div className="space-y-4">
                          <Field label="Prompt">
                            <LocalizedContent key={selectedNode.id} localizationKey={selectedConfig.localizationKey} localizationKeys={localizationKeys} selectClass={selectClass} onKeyChange={(localizationKey) => onNodeChange(selectedNode.id, { data: { config: { localizationKey } } })}>
                            <TemplateTextarea
                              value={selectedNode.data?.config?.prompt ?? selectedNode.data?.config?.message ?? (isQuickReplyNode ? normalizedQuickReply.prompt : '')}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: {
                                    prompt: value,
                                    message: value,
                                  },
                                },
                              })}
                              variables={variables}
                              className={`${inputClass} min-h-[100px] resize-none`}
                              placeholder="Ask a question"
                            />
                            </LocalizedContent>
                          </Field>
                          <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/70 p-3 dark:border-white/10 dark:bg-white/5">
                          <Field label="Translation language">
                            <select value={translationLanguage} onChange={(event) => setTranslationLanguage(event.target.value)} className={selectClass}>
                              {CONVERSATION_LANGUAGES.filter((language) => language.code !== 'en').map((language) => <option key={language.code} value={language.code}>{language.label} ({language.code})</option>)}
                            </select>
                          </Field>
                          <Field label={`${translationLanguageLabel} prompt`}>
                            <TemplateTextarea
                              value={selectedNode.data?.config?.translations?.[translationLanguage]?.prompt ?? selectedNode.data?.config?.translations?.[translationLanguage]?.message ?? ''}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: {
                                    translations: {
                                      ...(selectedNode.data?.config?.translations || {}),
                                      [translationLanguage]: {
                                        ...(selectedNode.data?.config?.translations?.[translationLanguage] || {}),
                                        prompt: value,
                                        message: value,
                                      },
                                    },
                                  },
                                },
                              })}
                              variables={variables}
                              className={`${inputClass} min-h-[100px] resize-none`}
                              placeholder={`Write the ${translationLanguageLabel} prompt`}
                            />
                          </Field>
                          </div>
                          <Field label="Variable name">
                            <VariableField
                              value={selectedNode.data?.config?.variable || selectedNode.data?.config?.variableName || ''}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: { variable: value, variableName: value },
                                },
                              })}
                              variables={variables}
                              placeholder="user_reply"
                            />
                          </Field>
                          <Field label="Placeholder">
                            <input
                              value={selectedNode.data?.config?.placeholder || ''}
                              onChange={(event) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: { placeholder: event.target.value },
                                },
                              })}
                              className={inputClass}
                              placeholder="Type your answer"
                            />
                          </Field>
                          <Checkbox
                            label="Required answer"
                            checked={Boolean(selectedNode.data?.config?.required)}
                            onChange={(value) => onNodeChange(selectedNode.id, {
                              data: {
                                config: { required: value },
                              },
                            })}
                          />
                          {isQuickReplyNode ? (
                            <>
                              <div className="mb-3 flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/70 p-2 dark:border-slate-800 dark:bg-slate-900/60">
                                <span className="text-[12px] font-semibold text-slate-700 dark:text-slate-300">Option Source</span>
                                <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 text-xs font-medium dark:border-slate-700 dark:bg-slate-800">
                                  <button
                                    type="button"
                                    onClick={() => onNodeChange(selectedNode.id, {
                                      data: { config: { optionsMode: 'static' } },
                                    })}
                                    className={`rounded-md px-2.5 py-1 transition ${
                                      (selectedNode.data?.config?.optionsMode || 'static') === 'static'
                                        ? 'bg-sky-500 font-semibold text-white shadow-xs'
                                        : 'text-slate-600 hover:text-slate-900 dark:text-slate-300'
                                    }`}
                                  >
                                    Static
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => onNodeChange(selectedNode.id, {
                                      data: { config: { optionsMode: 'dynamic' } },
                                    })}
                                    className={`rounded-md px-2.5 py-1 transition ${
                                      selectedNode.data?.config?.optionsMode === 'dynamic'
                                        ? 'bg-sky-500 font-semibold text-white shadow-xs'
                                        : 'text-slate-600 hover:text-slate-900 dark:text-slate-300'
                                    }`}
                                  >
                                    Dynamic (Variable)
                                  </button>
                                </div>
                              </div>

                              {selectedNode.data?.config?.optionsMode === 'dynamic' ? (
                                <div className="mb-3 rounded-xl border border-sky-200/80 bg-sky-50/50 p-3 dark:border-sky-500/20 dark:bg-sky-950/20">
                                  <div className="mb-1 text-[11px] font-semibold text-sky-800 dark:text-sky-300">
                                    Dynamic Options Variable
                                  </div>
                                  <p className="mb-2 text-[11px] text-slate-500 dark:text-slate-400">
                                    Map an array or object from a Function, API response, or Journey variable (e.g. <code>api_result.items</code> or <code>journey.menuOptions</code>).
                                  </p>
                                  <VariableField
                                    value={selectedNode.data?.config?.dynamicOptionsVariable || ''}
                                    onChange={(value) => onNodeChange(selectedNode.id, {
                                      data: { config: { dynamicOptionsVariable: value } },
                                    })}
                                    variables={variables}
                                    placeholder="journey.menuOptions or api_result.items"
                                  />
                                </div>
                              ) : (
                                <OptionsEditor
                                  key={selectedNode.id}
                                  value={normalizedQuickReply.options}
                                  onReorder={onReorderQuickReplyOptions ? (from, to) => onReorderQuickReplyOptions(selectedNode.id, from, to) : undefined}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: { options: value },
                                    },
                                  })}
                                  localizationKeys={localizationKeys}
                                />
                              )}
                              <QuickReplyRoutingEditor
                                rows={quickReplyBranchRows}
                                fallbackEdge={quickReplyFallbackEdge}
                                nextNodeOptions={nextNodeOptions}
                                onEdgeChange={onEdgeChange}
                                onCreateEdge={onCreateEdge}
                                onDeleteEdge={onDeleteEdge}
                                sourceNodeId={selectedNode.id}
                              />
                            </>
                          ) : null}
                          {isListNode ? (
                            <>
                              <Field label="List title">
                                <input
                                  value={selectedNode.data?.config?.title || selectedNode.data?.config?.prompt || ''}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: { title: event.target.value, prompt: event.target.value },
                                    },
                                  })}
                                  className={inputClass}
                                  placeholder="Choose one option"
                                />
                              </Field>
                              <ListItemsEditor
                                items={selectedNode.data?.config?.listItems || []}
                                onChange={(value) => onNodeChange(selectedNode.id, {
                                  data: {
                                    config: { listItems: value },
                                  },
                                })}
                              />
                            </>
                          ) : null}
                          <Field label="Validation">
                            <select
                              value={selectedNode.data?.config?.validation || 'none'}
                              onChange={(event) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: { validation: event.target.value },
                                },
                              })}
                              className={selectClass}
                            >
                              <option value="none">None</option>
                              <option value="email">Email</option>
                              <option value="name">Name</option>
                              <option value="phone">Phone</option>
                              <option value="date">Date</option>
                              <option value="number">Number</option>
                              <option value="text">Text</option>
                            </select>
                          </Field>
                          {selectedNode.data?.config?.validation === 'date' ? (
                            <DateValidationEditor
                              value={selectedNode.data?.config?.dateValidation}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: { dateValidation: value },
                                },
                              })}
                            />
                          ) : null}
                        </div>
                      )}

                      {selectedNode.type === 'condition' && (
                        isAbSplitNode ? (
                          <div className="space-y-4 rounded-2xl border border-purple-200 bg-purple-50/70 p-4 dark:border-purple-400/20 dark:bg-purple-500/10">
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-purple-800 dark:text-purple-300">
                              <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                              <span>A/B Split Test Configuration</span>
                            </div>
                            <p className="text-xs text-purple-900/80 dark:text-purple-200/80">
                              Randomly splits user traffic across Path A and Path B according to the configured percentage ratio.
                            </p>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between text-xs font-semibold text-slate-700 dark:text-slate-300">
                                <span>Traffic distribution ratio:</span>
                                <span className="font-mono font-bold text-sky-600 dark:text-sky-400">
                                  Path A: {Number.isFinite(Number(selectedNode.data?.config?.ratioA)) ? Number(selectedNode.data.config.ratioA) : 50}%
                                </span>
                              </div>
                              <input
                                type="range"
                                min="1"
                                max="99"
                                value={Number.isFinite(Number(selectedNode.data?.config?.ratioA)) ? Number(selectedNode.data.config.ratioA) : 50}
                                onChange={(e) => onNodeChange(selectedNode.id, { data: { config: { ratioA: Number(e.target.value) } } })}
                                className="w-full accent-purple-600"
                              />
                              <div className="flex items-center justify-between text-[11px] font-medium">
                                <span className="text-sky-600 dark:text-sky-400 font-bold">Path A: {Number.isFinite(Number(selectedNode.data?.config?.ratioA)) ? Number(selectedNode.data.config.ratioA) : 50}%</span>
                                <span className="text-pink-600 dark:text-pink-400 font-bold">Path B: {100 - (Number.isFinite(Number(selectedNode.data?.config?.ratioA)) ? Number(selectedNode.data.config.ratioA) : 50)}%</span>
                              </div>
                            </div>

                            <Field label="Save assigned variant to variable">
                              <input
                                type="text"
                                value={selectedNode.data?.config?.saveToVariable || 'ab_variant'}
                                onChange={(e) => onNodeChange(selectedNode.id, { data: { config: { saveToVariable: e.target.value } } })}
                                placeholder="ab_variant"
                                className={inputClass}
                              />
                            </Field>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {(Array.isArray(selectedNode.data?.config?.conditions) && selectedNode.data.config.conditions.length
                              ? selectedNode.data.config.conditions
                              : [{ conditionId: 'condition-0', variable: selectedNode.data?.config?.variable || '', operator: selectedNode.data?.config?.operator || 'equals', value: selectedNode.data?.config?.value ?? '', ignoreCase: false }]
                            ).map((rule, index, rules) => (
                              <div key={rule.conditionId || index} className="space-y-3 rounded-2xl border border-violet-200 bg-violet-50/60 p-3 dark:border-violet-400/20 dark:bg-violet-500/5">
                                <div className="flex items-center justify-between"><span className="text-xs font-bold text-violet-700 dark:text-violet-200">{index === 0 ? 'IF CONDITION' : 'ELSE IF CONDITION'}</span>{index > 0 && <button type="button" onClick={() => onNodeChange(selectedNode.id, { data: { config: { conditions: rules.filter((_, i) => i !== index) } } })} className="text-xs font-semibold text-rose-600">Remove</button>}</div>
                            <Field label="Variable">
                              <VariableField
                                value={rule.variable || ''}
                                onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { conditions: rules.map((item, i) => i === index ? { ...item, variable: value } : item) } } })}
                                variables={variables}
                                placeholder="customer_status"
                              />
                            </Field>
                            <Field label="Operator">
                              <select
                                value={rule.operator || 'equals'}
                                onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { conditions: rules.map((item, i) => i === index ? { ...item, operator: event.target.value } : item) } } })}
                                className={selectClass}
                              >
                                <option value="equals">Equals</option>
                                <option value="contains">Contains</option>
                                <option value="greater_than">Greater than</option>
                                <option value="less_than">Less than</option>
                                <option value="exists">Exists</option>
                                <option value="not_exists">Not exists</option>
                              </select>
                            </Field>
                            {!['exists', 'not_exists'].includes(rule.operator) && <Field label="Value">
                              <input
                                value={rule.value ?? ''}
                                onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { conditions: rules.map((item, i) => i === index ? { ...item, value: event.target.value } : item) } } })}
                                className={inputClass}
                                placeholder="active"
                              />
                            </Field>}
                            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={Boolean(rule.ignoreCase)} onChange={(event) => onNodeChange(selectedNode.id, { data: { config: { conditions: rules.map((item, i) => i === index ? { ...item, ignoreCase: event.target.checked } : item) } } })} />Ignore case</label>
                              </div>
                            ))}
                            <button type="button" onClick={() => { const rules = Array.isArray(selectedNode.data?.config?.conditions) && selectedNode.data.config.conditions.length ? selectedNode.data.config.conditions : [{ conditionId: 'condition-0', variable: selectedNode.data?.config?.variable || '', operator: selectedNode.data?.config?.operator || 'equals', value: selectedNode.data?.config?.value ?? '' }]; onNodeChange(selectedNode.id, { data: { config: { conditions: [...rules, { conditionId: `condition-${Date.now()}`, variable: '', operator: 'equals', value: '', ignoreCase: false }] } } }); }} className="rounded-xl border border-violet-300 px-4 py-2 text-sm font-semibold text-violet-700 dark:text-violet-200">+ Add another condition</button>
                            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-bold text-rose-700 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-200">ELSE — runs when no condition matches</div>
                          </div>
                        )
                      )}

                      {selectedNode.type === 'api_call' && (
                        <div className="space-y-4">
                          {supportsRequestSection ? (
                            <>
                              <Field label="Saved API">
                                <div className="space-y-3">
                                  <select
                                    value={selectedSavedApi?.apiId || ''}
                                    onChange={(event) => {
                                      const selectedApi = normalizedSavedApis.find((item) => item.apiId === event.target.value) || null;
                                      if (!selectedApi) {
                                        onNodeChange(selectedNode.id, {
                                          data: {
                                            config: {
                                              asyncRequest: {
                                                ...(selectedNode.data?.config?.asyncRequest || {}),
                                                apiDefinitionId: '',
                                                apiDefinitionName: '',
                                              },
                                            },
                                          },
                                        });
                                        return;
                                      }

                                      onNodeChange(selectedNode.id, {
                                        data: {
                                          config: {
                                            asyncRequest: {
                                              ...(selectedNode.data?.config?.asyncRequest || {}),
                                              apiDefinitionId: selectedApi.apiId,
                                              apiDefinitionName: selectedApi.name || '',
                                              method: selectedApi.method || 'GET',
                                              url: selectedApi.url || '',
                                              headers: selectedApi.headers || {},
                                              query: selectedApi.query || {},
                                              body: selectedApi.body ?? {},
                                              auth: selectedApi.auth || { type: 'none' },
                                              variableMappings: selectedNode.data?.config?.asyncRequest?.variableMappings
                                                || selectedNode.data?.config?.variableMappings
                                                || Object.fromEntries(getRequestTemplateVariables(selectedApi).map((name) => [name, name])),
                                            },
                                          },
                                        },
                                      });
                                    }}
                                    className={selectClass}
                                  >
                                    <option value="">Select saved API</option>
                                    {normalizedSavedApis.map((api) => (
                                      <option key={api.apiId} value={api.apiId}>
                                        {api.name} - {api.method}
                                      </option>
                                    ))}
                                  </select>
                                  {normalizedSavedApis.length === 0 ? (
                                    <div className="rounded-2xl border border-amber-400/15 bg-amber-500/10 px-4 py-3 text-xs leading-5 text-amber-100">
                                      No saved APIs found for this bot and environment yet. Add one in API Management, then come back here and select it.
                                    </div>
                                  ) : null}
                                  <a
                                    href="/api-management"
                                    className="inline-flex items-center rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-medium text-cyan-700 transition hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100 dark:hover:bg-cyan-500/15"
                                  >
                                    Open API Management
                                  </a>
                                </div>
                              </Field>
                              <Field label="Endpoint URL">
                                <input
                                  value={selectedNode.data?.config?.asyncRequest?.url || selectedNode.data?.config?.url || ''}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          url: event.target.value,
                                        },
                                      },
                                    },
                                  })}
                                  className={inputClass}
                                  placeholder="https://api.example.com/customer"
                                />
                              </Field>
                              <RequestVariableMappingEditor
                                placeholders={requestTemplateVariables}
                                mappings={requestConfig.variableMappings || {}}
                                variables={variables}
                                onChange={(variableMappings) => onNodeChange(selectedNode.id, {
                                  data: {
                                    config: {
                                      asyncRequest: {
                                        ...(selectedNode.data?.config?.asyncRequest || {}),
                                        variableMappings,
                                      },
                                    },
                                  },
                                })}
                              />
                            </>
                          ) : null}
                          {isVariableNode ? (
                            <VariableAssignmentsEditor
                              items={selectedNode.data?.config?.variableAssignments || []}
                              variables={variables}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: {
                                    variableAssignments: value,
                                  },
                                },
                              })}
                            />
                          ) : null}
                          {isDatabaseNode ? (
                            <>
                              <Field label="Custom table">
                                <select
                                  value={selectedNode.data?.config?.customStorage?.tableId || ''}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        customStorage: {
                                          ...(selectedNode.data?.config?.customStorage || {}),
                                          enabled: true,
                                          tableId: event.target.value,
                                          filterRules: [],
                                          filters: {},
                                          payload: {},
                                        },
                                      },
                                    },
                                  })}
                                  className={selectClass}
                                >
                                  <option value="">Select custom table</option>
                                  {customTables.map((table) => (
                                    <option key={table.tableId} value={table.tableId}>
                                      {table.name}
                                    </option>
                                  ))}
                                </select>
                                {customTables.length === 0 ? (
                                  <a
                                    href="/data/custom"
                                    className="mt-3 inline-flex rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2 text-xs font-medium text-cyan-700 transition hover:bg-cyan-100 dark:border-cyan-400/20 dark:bg-cyan-500/10 dark:text-cyan-100"
                                  >
                                    Create a custom table
                                  </a>
                                ) : null}
                              </Field>
                              <Field label="Action">
                                <select
                                  value={selectedNode.data?.config?.customStorage?.operation || 'create'}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        customStorage: {
                                          ...(selectedNode.data?.config?.customStorage || {}),
                                          enabled: true,
                                          operation: event.target.value,
                                          ...(event.target.value === 'find_many' ? { payload: {} } : {}),
                                        },
                                      },
                                    },
                                  })}
                                  className={selectClass}
                                >
                                  <option value="create">Insert</option>
                                  <option value="find_many">Search</option>
                                  <option value="update">Search and update</option>
                                </select>
                              </Field>
                              {['find_many', 'update'].includes(selectedNode.data?.config?.customStorage?.operation || 'create') ? (
                                <Field label="Search filters">
                                  <DatabaseFilterEditor
                                    rules={selectedNode.data?.config?.customStorage?.filterRules || Object.entries(selectedNode.data?.config?.customStorage?.filters || {}).map(([field, value], index) => ({ id: `legacy-filter-${index}`, field, operator: 'equals', value }))}
                                    table={selectedCustomTable}
                                    variables={variables}
                                    required={(selectedNode.data?.config?.customStorage?.operation || 'create') === 'update'}
                                    onChange={(filterRules) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                        customStorage: {
                                          ...(selectedNode.data?.config?.customStorage || {}),
                                          enabled: true,
                                          filterRules,
                                          filters: {},
                                          },
                                        },
                                      },
                                    })}
                                  />
                                </Field>
                              ) : null}
                              {(selectedNode.data?.config?.customStorage?.operation || 'create') === 'find_many' ? (
                                <>
                                <Field label="Maximum search results">
                                  <input
                                    type="number"
                                    min="1"
                                    max="200"
                                    value={selectedNode.data?.config?.customStorage?.limit || 25}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                        customStorage: {
                                          ...(selectedNode.data?.config?.customStorage || {}),
                                          enabled: true,
                                          limit: Math.max(1, Math.min(200, Number(event.target.value || 25))),
                                        },
                                        },
                                      },
                                    })}
                                    className={inputClass}
                                    placeholder="25"
                                  />
                                </Field>
                                <label className="flex items-start gap-2 text-sm text-slate-700">
                                  <input
                                    type="checkbox"
                                    className="mt-1"
                                    checked={selectedNode.data?.config?.customStorage?.failOnEmptyResult !== false}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          customStorage: {
                                            ...(selectedNode.data?.config?.customStorage || {}),
                                            enabled: true,
                                            failOnEmptyResult: event.target.checked,
                                          },
                                        },
                                      },
                                    })}
                                  />
                                  <span className="font-semibold">Use Failure path when no records match</span>
                                </label>
                                </>
                              ) : null}
                              {(selectedNode.data?.config?.customStorage?.operation || 'create') !== 'find_many' ? (
                                <Field
                                  label={(selectedNode.data?.config?.customStorage?.operation || 'create') === 'update' ? 'Update values' : 'Insert values'}
                                >
                                  <DatabaseValueEditor
                                    value={selectedNode.data?.config?.customStorage?.payload || {}}
                                    table={selectedCustomTable}
                                    variables={variables}
                                    label={(selectedNode.data?.config?.customStorage?.operation || 'create') === 'update' ? 'Update field' : 'Insert field'}
                                    onChange={(value) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          customStorage: {
                                            ...(selectedNode.data?.config?.customStorage || {}),
                                            enabled: true,
                                            payload: value,
                                          },
                                        },
                                      },
                                    })}
                                  />
                                </Field>
                              ) : null}
                              <Field label="Store result in variable">
                                <VariableField
                                  value={selectedNode.data?.config?.customStorage?.responseVariable || ''}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        customStorage: {
                                          ...(selectedNode.data?.config?.customStorage || {}),
                                          enabled: true,
                                          responseVariable: value,
                                        },
                                      },
                                    },
                                  })}
                                  variables={variables}
                                  placeholder="customer_record"
                                />
                              </Field>
                            </>
                          ) : null}
                          {supportsRequestSection ? (
                            <>
                              <Field label="Method">
                                <select
                                  value={selectedNode.data?.config?.asyncRequest?.method || selectedNode.data?.config?.method || 'POST'}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          method: event.target.value,
                                        },
                                      },
                                    },
                                  })}
                                  className={selectClass}
                                >
                                  <option value="GET">GET</option>
                                  <option value="POST">POST</option>
                                  <option value="PUT">PUT</option>
                                  <option value="PATCH">PATCH</option>
                                  <option value="DELETE">DELETE</option>
                                </select>
                              </Field>
                              <Field label="Headers">
                                <JsonArea
                                  value={selectedNode.data?.config?.asyncRequest?.headers || selectedNode.data?.config?.headers || {}}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          headers: value,
                                        },
                                      },
                                    },
                                  })}
                                  placeholder='{"Authorization":"Bearer token"}'
                                />
                              </Field>
                              <Field label="Query params">
                                <JsonArea
                                  value={selectedNode.data?.config?.asyncRequest?.query || selectedNode.data?.config?.query || {}}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          query: value,
                                        },
                                      },
                                    },
                                  })}
                                  placeholder='{"status":"active"}'
                                />
                              </Field>
                              <Field label="Path params">
                                <JsonArea
                                  value={selectedNode.data?.config?.asyncRequest?.pathParams || selectedNode.data?.config?.pathParams || {}}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          pathParams: value,
                                        },
                                      },
                                    },
                                  })}
                                  placeholder='{"id":"{{customer_id}}"}'
                                />
                              </Field>
                              <Field label="Auth type">
                                <select
                                  value={selectedNode.data?.config?.asyncRequest?.auth?.type || 'none'}
                                  onChange={(event) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          auth: {
                                            ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                            type: event.target.value,
                                          },
                                        },
                                      },
                                    },
                                  })}
                                  className={selectClass}
                                >
                                  <option value="none">None</option>
                                  <option value="bearer">Bearer token</option>
                                  <option value="api_key">API key</option>
                                  <option value="basic">Basic auth</option>
                                </select>
                              </Field>
                              {(selectedNode.data?.config?.asyncRequest?.auth?.type || 'none') === 'bearer' ? (
                                <Field label="Bearer token">
                                  <input
                                    value={selectedNode.data?.config?.asyncRequest?.auth?.token || ''}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          asyncRequest: {
                                            ...(selectedNode.data?.config?.asyncRequest || {}),
                                            auth: {
                                              ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                              type: 'bearer',
                                              token: event.target.value,
                                            },
                                          },
                                        },
                                      },
                                    })}
                                    className={inputClass}
                                    placeholder="{{api_token}}"
                                  />
                                </Field>
                              ) : null}
                              {(selectedNode.data?.config?.asyncRequest?.auth?.type || 'none') === 'api_key' ? (
                                <>
                                  <Field label="API key header">
                                    <input
                                      value={selectedNode.data?.config?.asyncRequest?.auth?.header || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: {
                                            asyncRequest: {
                                              ...(selectedNode.data?.config?.asyncRequest || {}),
                                              auth: {
                                                ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                                type: 'api_key',
                                                header: event.target.value,
                                              },
                                            },
                                          },
                                        },
                                      })}
                                      className={inputClass}
                                      placeholder="x-api-key"
                                    />
                                  </Field>
                                  <Field label="API key value">
                                    <input
                                      value={selectedNode.data?.config?.asyncRequest?.auth?.value || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: {
                                            asyncRequest: {
                                              ...(selectedNode.data?.config?.asyncRequest || {}),
                                              auth: {
                                                ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                                type: 'api_key',
                                                value: event.target.value,
                                              },
                                            },
                                          },
                                        },
                                      })}
                                      className={inputClass}
                                      placeholder="{{secret_key}}"
                                    />
                                  </Field>
                                </>
                              ) : null}
                              {(selectedNode.data?.config?.asyncRequest?.auth?.type || 'none') === 'basic' ? (
                                <>
                                  <Field label="Basic auth username">
                                    <input
                                      value={selectedNode.data?.config?.asyncRequest?.auth?.username || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: {
                                            asyncRequest: {
                                              ...(selectedNode.data?.config?.asyncRequest || {}),
                                              auth: {
                                                ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                                type: 'basic',
                                                username: event.target.value,
                                              },
                                            },
                                          },
                                        },
                                      })}
                                      className={inputClass}
                                      placeholder="{{api_username}}"
                                    />
                                  </Field>
                                  <Field label="Basic auth password">
                                    <input
                                      type="password"
                                      value={selectedNode.data?.config?.asyncRequest?.auth?.password || ''}
                                      onChange={(event) => onNodeChange(selectedNode.id, {
                                        data: {
                                          config: {
                                            asyncRequest: {
                                              ...(selectedNode.data?.config?.asyncRequest || {}),
                                              auth: {
                                                ...(selectedNode.data?.config?.asyncRequest?.auth || {}),
                                                type: 'basic',
                                                password: event.target.value,
                                              },
                                            },
                                          },
                                        },
                                      })}
                                      className={inputClass}
                                      placeholder="{{api_password}}"
                                    />
                                  </Field>
                                </>
                              ) : null}
                              <Field label="Body">
                                <JsonArea
                                  value={selectedNode.data?.config?.asyncRequest?.body || selectedNode.data?.config?.body || {}}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          body: value,
                                        },
                                      },
                                    },
                                  })}
                                  placeholder='{"phone":"{{user_phone}}"}'
                                  minHeight="120px"
                                />
                              </Field>
                              <div className="grid gap-4 md:grid-cols-3">
                                <Field label="Timeout (ms)">
                                  <input
                                    type="number"
                                    min="100"
                                    max="60000"
                                    value={selectedNode.data?.config?.asyncRequest?.timeout || 10000}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          asyncRequest: {
                                            ...(selectedNode.data?.config?.asyncRequest || {}),
                                            timeout: Number(event.target.value || 10000),
                                          },
                                        },
                                      },
                                    })}
                                    className={inputClass}
                                    placeholder="10000"
                                  />
                                </Field>
                                <Field label="Retries">
                                  <input
                                    type="number"
                                    min="0"
                                    max="3"
                                    value={selectedNode.data?.config?.asyncRequest?.retries || 0}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          asyncRequest: {
                                            ...(selectedNode.data?.config?.asyncRequest || {}),
                                            retries: Number(event.target.value || 0),
                                          },
                                        },
                                      },
                                    })}
                                    className={inputClass}
                                    placeholder="2"
                                  />
                                </Field>
                                <Field label="Retry delay (ms)">
                                  <input
                                    type="number"
                                    min="0"
                                    max="10000"
                                    value={selectedNode.data?.config?.asyncRequest?.retryDelayMs || 0}
                                    onChange={(event) => onNodeChange(selectedNode.id, {
                                      data: {
                                        config: {
                                          asyncRequest: {
                                            ...(selectedNode.data?.config?.asyncRequest || {}),
                                            retryDelayMs: Number(event.target.value || 0),
                                          },
                                        },
                                      },
                                    })}
                                    className={inputClass}
                                    placeholder="300"
                                  />
                                </Field>
                              </div>
                              <Field label="API response variable">
                                <VariableField
                                  value={selectedNode.data?.config?.asyncRequest?.responseVariable || ''}
                                  onChange={(value) => onNodeChange(selectedNode.id, {
                                    data: {
                                      config: {
                                        asyncRequest: {
                                          ...(selectedNode.data?.config?.asyncRequest || {}),
                                          responseVariable: value,
                                        },
                                      },
                                    },
                                  })}
                                  variables={variables}
                                  placeholder="api_result"
                                />
                              </Field>
                            </>
                          ) : null}
                          {supportsResponseMapping ? (
                            <ResponseMappingEditor
                              items={selectedNode.data?.config?.responseMapping || []}
                              variables={variables}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: {
                                    responseMapping: value,
                                  },
                                },
                              })}
                            />
                          ) : null}
                          {!isFunctionNode && selectedNode.type !== 'api_call' ? <Field label="Response message">
                            <TemplateTextarea
                              value={selectedNode.data?.config?.responseMessage || ''}
                              onChange={(value) => onNodeChange(selectedNode.id, {
                                data: {
                                  config: {
                                    responseMessage: value,
                                  },
                                },
                              })}
                              variables={variables}
                              className={`${inputClass} min-h-[90px] resize-none`}
                              placeholder="Thanks, I found your record."
                            />
                          </Field> : null}
                          {isFunctionNode ? (
                            <>
                              <Field label="Saved function">
                                <select
                                  value={selectedNode.data?.config?.functionDefinitionId || ''}
                                  onChange={(event) => {
                                    /** Purpose: Find the selected saved-function definition. Why: Configuration must use the chosen function's metadata. */
                                    const selectedFunction = normalizedSavedFunctions.find((item) => item.functionId === event.target.value);
                                    onNodeChange(selectedNode.id, { data: { config: {
                                      functionDefinitionId: selectedFunction?.functionId || '',
                                      functionDefinitionName: selectedFunction?.name || '',
                                      code: selectedFunction?.code || '',
                                      timeout: selectedFunction?.timeout || 5000,
                                    } } });
                                  }}
                                  className={inputClass}
                                >
                                  <option value="">Select a saved function</option>
                                  {normalizedSavedFunctions.map((item) => <option key={item.functionId} value={item.functionId}>{item.name}</option>)}
                                </select>
                                {selectedNode.data?.config?.functionDefinitionId ? (
                                  <a
                                    href={`/function-management?functionId=${encodeURIComponent(selectedNode.data.config.functionDefinitionId)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-cyan-700 hover:underline dark:text-cyan-300"
                                  >
                                    Open function ↗
                                  </a>
                                ) : null}
                              </Field>
                              <Field label="Store returned value in">
                                <VariableField
                                  value={selectedNode.data?.config?.outputVariable || ''}
                                  onChange={(value) => onNodeChange(selectedNode.id, { data: { config: { outputVariable: value } } })}
                                  variables={variables}
                                  placeholder="function_result"
                                />
                              </Field>
                            </>
                          ) : null}
                        </div>
                      )}
                    </div>

                    <div className="mt-6 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={onFitView}
                        className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition hover:border-cyan-300 hover:bg-cyan-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-200 dark:hover:bg-white/10"
                      >
                        Fit canvas
                      </button>
                      <button
                        type="button"
                        onClick={() => onExportJson?.()}
                        className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-700 transition hover:bg-violet-100 dark:border-violet-400/20 dark:bg-violet-500/10 dark:text-violet-100 dark:hover:bg-violet-500/15"
                      >
                        Export JSON
                      </button>
                      <button
                        type="button"
                        onClick={() => onDeleteNode?.(selectedNode.id)}
                        disabled={selectedNode.type === 'start' || selectedNode.deletable === false}
                        className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-medium text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
                      >
                        Delete node
                      </button>
                    </div>
                  </div>
                ) : selectedEdge ? (
                  <div className={PANEL_CARD_CLASS}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="text-xs uppercase tracking-[0.24em] text-slate-500 dark:text-slate-500">
                          Selected connection
                        </div>
                        <div className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">
                          {selectedEdge.label || selectedEdge.data?.condition || 'Flow link'}
                        </div>
                        <div className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                          {selectedEdge.source} -> {selectedEdge.target}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => onDeleteEdge?.(selectedEdge.id)}
                          className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 dark:border-rose-400/20 dark:bg-rose-500/10 dark:text-rose-100 dark:hover:bg-rose-500/15"
                        >
                          Delete link
                        </button>
                        <button
                          type="button"
                          onClick={onClearEdgeSelection}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                        >
                          Clear
                        </button>
                      </div>
                    </div>

                    <div className="mt-5 space-y-4">
                      <Field label="Connection label">
                        <input
                          value={selectedEdge.label || ''}
                          onChange={(event) => onEdgeChange?.(selectedEdge.id, {
                            label: event.target.value,
                          })}
                          className={inputClass}
                          placeholder="next"
                        />
                      </Field>

                      <Field label="Condition key">
                        <input
                          value={selectedEdge.data?.condition || ''}
                          onChange={(event) => onEdgeChange?.(selectedEdge.id, {
                            data: {
                              ...(selectedEdge.data || {}),
                              condition: event.target.value,
                            },
                          })}
                          className={inputClass}
                          placeholder="next"
                        />
                      </Field>

                    </div>
                  </div>
                ) : (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-white/80 p-6 text-sm leading-7 text-slate-500 shadow-sm dark:border-white/10 dark:bg-slate-900/60 dark:text-slate-400">
                    Select a node to edit its content, variable, or branch rules. You can also click a connection line to rename or disconnect it.
                  </div>
                )}
              </div>
            )}
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
